/**
 * Chaque chiffre dit d'où il vient : le calcul, ligne par ligne, et la source
 * officielle de chaque taux.
 *
 * UNE EXPLICATION FAUSSE SERAIT PIRE QU'AUCUNE. Chaque étape porte donc, en
 * plus de son texte, l'opération qui la produit (un produit, une somme, un
 * quotient, ou « moteur » quand le chiffre sort d'un barème entier, comme
 * l'impôt d'un foyer). npm run check:explanations refait chaque opération et
 * vérifie qu'elle retombe sur la valeur écrite, puis que la dernière étape est
 * exactement le montant affiché par l'outil. Le texte ne peut pas mentir sur
 * le calcul sans que le contrôle échoue.
 *
 * LES MOTEURS NE CHANGENT PAS. Les explications relisent les fonctions des
 * outils (microEnterprise, incomeTax, dayRate, holding) et leurs constantes :
 * un taux qui bouge au 1er janvier bouge dans l'outil et dans son explication
 * en même temps.
 */

import {
    HEURES_PAR_TRIMESTRE,
    PALIERS_CIPAV,
    PART_RETRAITE_BASE,
    REVENU_PAR_TRIMESTRE,
    SMIC_HORAIRE_REFERENCE,
    TAUX_COTISATIONS,
    TAUX_RETRAITE_BASE,
    activiteFiscale,
    revenuNet,
    tauxCfp,
} from "./microEnterprise.js";
import {
    ABATTEMENT_MINIMUM,
    ACTIVITES,
    IJ,
    beneficeMicro,
    comparerVersementLiberatoire,
    impotBrut,
    impotSurLeRevenu,
} from "./incomeTax.js";
import { joursFeriesEnSemaine, joursOuvres } from "./dayRate.js";
import {
    ABATTEMENT_FRAIS_MAXIMUM,
    ABATTEMENT_FRAIS_MINIMUM,
    ABATTEMENT_FRAIS_PROFESSIONNELS,
    IS_PLAFOND_TAUX_REDUIT,
    IS_TAUX_NORMAL,
    IS_TAUX_REDUIT,
    PFU_IR,
    PFU_SOCIAL,
    PASS_2026,
    TAUX_CFP_INDEPENDANT,
    TAUX_CSG_CRDS,
    abattementFraisProfessionnels,
    familleTns,
    cotisationsIndependant,
    impotSocietes,
} from "./businessStructure.js";
import { QUOTE_PART_MERE_FILLE, QUOTE_PART_PLUS_VALUE, reinvestir, revendre } from "./holding.js";
import { ABATTEMENT_DIVIDENDES_BAREME, SEUIL_DIVIDENDES_EURL, voletRemuneration } from "./salaryDividends.js";
import { BAREME_IK } from "./mileageAllowance.js";
import { IJ_SOCIETE } from "./companySickLeave.js";
import { coutPourNet } from "./monthlyPayout.js";
import { CONGE_SUPPLEMENTAIRE, IJ_FORFAITAIRE, PASS as PASS_NAISSANCE } from "./parentalLeave.js";
import { STRIPE_BILLING, STRIPE_CARTE_EEE, STRIPE_FIXE, bilanAnnuel, commissionStripe } from "./saasSubscribers.js";

/* ------------------------------------------------------------------------- */
/* Écriture                                                                   */
/* ------------------------------------------------------------------------- */

export function eur(v, lang = "fr") {
    return `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;
}

export function pct(taux, lang = "fr") {
    const nombre = Math.round(taux * 1000) / 10;
    const texte = lang === "fr" ? String(nombre).replace(".", ",") : String(nombre);
    return lang === "fr" ? `${texte} %` : `${texte}%`;
}

/**
 * Un montant au centime, pour les indemnités par jour : 65,84 € ne s'arrondit
 * pas à 66 €. `decimales` à 3 pour un montant que la loi fixe au millième,
 * comme l'indemnité réduite au dixième (6,584 €) : l'arrondir fausserait le
 * produit que le lecteur refait.
 */
export function eurCentimes(v, lang = "fr", decimales = 2) {
    return `${v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: decimales })} €`;
}

/** Un taux à deux décimales, quand la règle en porte deux : 43,45 %, 17,87 %. */
export function pctPrecis(taux, lang = "fr") {
    const texte = (taux * 100).toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return lang === "fr" ? `${texte}\u00a0%` : `${texte}%`;
}

/* ------------------------------------------------------------------------- */
/* Les opérations, que le contrôle sait refaire                               */
/* ------------------------------------------------------------------------- */

export const produit = (...termes) => ({ type: "produit", termes });
export const somme = (...termes) => ({ type: "somme", termes });
export const quotientArrondiSup = (a, b) => ({ type: "quotientArrondiSup", termes: [a, b] });
export const quotient = (a, b) => ({ type: "quotient", termes: [a, b] });
export const plancher = (terme) => ({ type: "plancher", termes: [terme] });
export const plancherZero = (terme) => ({ type: "plancherZero", termes: [terme] });
export const minimum = (...termes) => ({ type: "minimum", termes });
export const maximum = (...termes) => ({ type: "maximum", termes });
export const MOTEUR = { type: "moteur" };

/** La valeur d'une opération, récursivement. `null` pour un chiffre de moteur. */
export function evaluer(op) {
    if (typeof op === "number") return op;
    if (!op || op.type === "moteur") return null;
    const v = op.termes.map(evaluer);
    if (v.some((x) => x === null)) return null;
    if (op.type === "produit") return v.reduce((a, b) => a * b, 1);
    if (op.type === "somme") return v.reduce((a, b) => a + b, 0);
    if (op.type === "quotientArrondiSup") return Math.ceil(v[0] / v[1]);
    if (op.type === "quotient") return v[0] / v[1];
    // La marge absorbe l'erreur d'arrondi d'un produit décimal, comme joursFactures.
    if (op.type === "plancher") return Math.floor(v[0] + 1e-9);
    if (op.type === "plancherZero") return Math.max(0, v[0]);
    if (op.type === "minimum") return Math.min(...v);
    if (op.type === "maximum") return Math.max(...v);
    return null;
}

const etape = (libelle, calcul, valeur, operation = MOTEUR, unite = "euros") => ({ libelle, calcul, valeur, operation, unite });

/** L'impôt sur les sociétés d'un bénéfice, écrit tranche par tranche. */
function etapeIs(libelle, benefice, lang) {
    const b = Math.max(0, benefice);
    const bas = Math.min(b, IS_PLAFOND_TAUX_REDUIT);
    const haut = Math.max(0, b - IS_PLAFOND_TAUX_REDUIT);
    const calcul =
        haut > 0
            ? `${eur(bas, lang)} × ${pct(IS_TAUX_REDUIT, lang)} + ${eur(haut, lang)} × ${pct(IS_TAUX_NORMAL, lang)}`
            : `${eur(bas, lang)} × ${pct(IS_TAUX_REDUIT, lang)}`;
    const operation = haut > 0 ? somme(produit(bas, IS_TAUX_REDUIT), produit(haut, IS_TAUX_NORMAL)) : produit(bas, IS_TAUX_REDUIT);
    return etape(libelle, calcul, impotSocietes(b), operation);
}

/* ------------------------------------------------------------------------- */
/* Micro-entreprise : revenu net, et tarif journalier                          */
/* ------------------------------------------------------------------------- */

const SOURCE_TAUX = {
    vente: "taux_global_bic_vente",
    services: "taux_global_bic_services",
    bnc: "taux_global_bnc_regime_general",
    cipav: "taux_global_cipav",
};
const SOURCE_ABATTEMENT = { vente: "abattement_bic_vente", services: "abattement_bic_services", bnc: "abattement_bnc" };

const TEXTES = {
    fr: {
        cotisations: "Chiffre d'affaires × taux de cotisations",
        cfp: "Chiffre d'affaires × taux de la contribution",
        benefice: "Bénéfice imposable : chiffre d'affaires moins l'abattement",
        beneficeMinimum: "Bénéfice imposable : chiffre d'affaires moins l'abattement minimum",
        revenuFoyer: "Revenu imposable du foyer, avec vos autres revenus",
        impotAvec: "Impôt de votre foyer avec cette activité (barème, parts, décote)",
        impotSans: "Impôt de votre foyer sans elle",
        impotDifference: "Ce que l'activité ajoute à l'impôt du foyer",
        vfl: "Chiffre d'affaires × taux du versement libératoire",
        vflAutres: "Impôt de vos autres revenus, au taux moyen du foyer",
        vflAvec: "Impôt de votre foyer avec l'option",
        net: "Chiffre d'affaires moins tout ce qui est prélevé",
        netFrais: "Chiffre d'affaires moins les prélèvements et vos frais",
        frais: "Frais mensuels × 12",
        joursOuvres: "Jours ouvrés de l'année, moins les jours fériés en semaine",
        joursFactures: "À {jours} jours par semaine, moins {semaines} semaines sans facturer, arrondi au jour inférieur",
        chiffreAffaires: "Chiffre d'affaires le plus bas qui vous laisse {objectif} par mois, frais payés",
        tarif: "Chiffre d'affaires ÷ jours facturés, arrondi à l'euro supérieur",
        euros: "calculé par le simulateur, à l'euro près",
        sur: "sur {montant} de revenu imposable",
        surAutres: "sur vos {montant} d'autres revenus",
    },
    en: {
        cotisations: "Turnover × contribution rate",
        cfp: "Turnover × training contribution rate",
        benefice: "Taxable profit: turnover minus the allowance",
        beneficeMinimum: "Taxable profit: turnover minus the minimum allowance",
        revenuFoyer: "Household taxable income, with your other income",
        impotAvec: "Your household's tax with this business (scale, shares, reduction)",
        impotSans: "Your household's tax without it",
        impotDifference: "What the business adds to the household's tax",
        vfl: "Turnover × flat-rate tax payment rate",
        vflAutres: "Tax on your other income, at the household's average rate",
        vflAvec: "Your household's tax with the option",
        net: "Turnover minus everything deducted",
        netFrais: "Turnover minus the deductions and your costs",
        frais: "Monthly costs × 12",
        joursOuvres: "Working days in the year, minus public holidays on weekdays",
        joursFactures: "At {jours} days a week, minus {semaines} weeks without billing, rounded down to the day",
        chiffreAffaires: "The lowest turnover that leaves you {objectif} a month, costs paid",
        tarif: "Turnover ÷ days billed, rounded up to the euro",
        euros: "worked out by the calculator, to the euro",
        sur: "on {montant} of taxable income",
        surAutres: "on your {montant} of other income",
    },
};

const remplir = (texte, valeurs) => texte.replace(/\{(\w+)\}/g, (m, k) => (valeurs[k] ?? m));

/** « le plus petit de 2 500 € et 2 613,83 € », l'écriture d'un minimum. */
const plusPetit = (a, b, lang) => (lang === "en" ? `the lower of ${a} and ${b}` : `le plus petit de ${a} et ${b}`);
/** Et celle d'un maximum. */
const plusGrand = (a, b, lang) => (lang === "en" ? `the higher of ${a} and ${b}` : `le plus grand de ${a} et ${b}`);

/**
 * Le bénéfice imposable d'une micro-entreprise, abattement ou abattement
 * minimum, et ses sources. Le même pour l'impôt, le versement libératoire et
 * les indemnités journalières.
 */
function etapeBenefice(ca, fiscale, lang, libelles = TEXTES[lang] || TEXTES.fr) {
    const abattement = ACTIVITES[fiscale].abattement;
    const retenu = Math.min(ca, Math.max(ca * abattement, ABATTEMENT_MINIMUM));
    const minimum = ca * abattement < ABATTEMENT_MINIMUM;
    return {
        etape: etape(
            minimum ? libelles.beneficeMinimum : libelles.benefice,
            minimum ? `${eur(ca, lang)} − ${eur(retenu, lang)}` : `${eur(ca, lang)} − ${eur(retenu, lang)} (${pct(abattement, lang)})`,
            beneficeMicro(ca, fiscale),
            somme(ca, -retenu),
        ),
        sources: minimum ? [SOURCE_ABATTEMENT[fiscale], "abattement_minimum"] : [SOURCE_ABATTEMENT[fiscale]],
    };
}

/** Les sources de l'impôt d'un foyer : barème, et selon le cas parts et décote. */
function sourcesImpotFoyer(revenu, foyer) {
    const sources = ["bareme_ir_2026_complet"];
    if ((foyer.enfants || 0) > 0) sources.push("plafond_quotient_familial_2026");
    if (impotBrut(revenu, foyer) - impotSurLeRevenu(revenu, foyer) > 0.5) sources.push("decote_ir_2026");
    return sources;
}

/**
 * Les explications d'un revenu net de micro-entreprise : cotisations,
 * contribution à la formation, impôt, et ce qu'il reste.
 *
 * @param p      les paramètres de `revenuNet`
 * @param frais  frais annuels à retirer du net (outil « Combien facturer »)
 */
export function expliquerMicro(p, lang = "fr", { frais = 0 } = {}) {
    const T = TEXTES[lang] || TEXTES.fr;
    const r = revenuNet(p);
    const ca = p.chiffreAffaires;
    const fiscale = activiteFiscale(p.activite);
    const taux = TAUX_COTISATIONS[p.activite];
    const tcfp = tauxCfp(p.activite, p.artisan);
    const foyer = { enCouple: p.enCouple, enfants: p.enfants || 0, parentIsole: p.parentIsole };
    const autres = p.autresRevenus || 0;

    const cotisations = {
        etapes: [etape(T.cotisations, `${eur(ca, lang)} × ${pct(taux, lang)}`, r.cotisations, produit(ca, taux))],
        sources: [SOURCE_TAUX[p.activite]],
    };
    const cfp = {
        etapes: [etape(T.cfp, `${eur(ca, lang)} × ${pct(tcfp, lang)}`, r.cfp, produit(ca, tcfp))],
        sources: ["cfp_taux"],
    };

    const comp = comparerVersementLiberatoire({ chiffreAffaires: ca, activite: fiscale, autresRevenus: autres, ...foyer });
    const sans = impotSurLeRevenu(autres, foyer);
    const etapesImpot = [];
    const sourcesImpot = [];
    if (p.optionFiscale === "vfl") {
        const vfl = ACTIVITES[fiscale].vfl;
        etapesImpot.push(etape(T.vfl, `${eur(ca, lang)} × ${pct(vfl, lang)}`, comp.vflSurCa, produit(ca, vfl)));
        sourcesImpot.push("vfl_taux");
        if (autres > 0) {
            etapesImpot.push(etape(T.vflAutres, remplir(T.surAutres, { montant: eur(autres, lang) }), comp.impotAutresRevenusEnVfl));
            etapesImpot.push(
                etape(T.vflAvec, `${eur(comp.vflSurCa, lang)} + ${eur(comp.impotAutresRevenusEnVfl, lang)}`, comp.impotVfl, somme(comp.vflSurCa, comp.impotAutresRevenusEnVfl)),
            );
            etapesImpot.push(etape(T.impotSans, remplir(T.surAutres, { montant: eur(autres, lang) }), sans));
            etapesImpot.push(etape(T.impotDifference, `${eur(comp.impotVfl, lang)} − ${eur(sans, lang)}`, r.impot, plancherZero(somme(comp.impotVfl, -sans))));
            sourcesImpot.push("bareme_ir_2026_complet");
        }
    } else {
        const benefice = etapeBenefice(ca, fiscale, lang, T);
        etapesImpot.push(benefice.etape);
        sourcesImpot.push(...benefice.sources);
        if (autres > 0) {
            etapesImpot.push(etape(T.revenuFoyer, `${eur(comp.benefice, lang)} + ${eur(autres, lang)}`, comp.revenuTotal, somme(comp.benefice, autres)));
        }
        etapesImpot.push(etape(T.impotAvec, remplir(T.sur, { montant: eur(comp.revenuTotal, lang) }), comp.impotBareme));
        if (autres > 0) {
            etapesImpot.push(etape(T.impotSans, remplir(T.surAutres, { montant: eur(autres, lang) }), sans));
            etapesImpot.push(etape(T.impotDifference, `${eur(comp.impotBareme, lang)} − ${eur(sans, lang)}`, r.impot, plancherZero(somme(comp.impotBareme, -sans))));
        }
        sourcesImpot.push("bareme_ir_2026_complet");
        if ((p.enfants || 0) > 0) sourcesImpot.push("plafond_quotient_familial_2026");
        if (impotBrut(comp.revenuTotal, foyer) - comp.impotBareme > 0.5) sourcesImpot.push("decote_ir_2026");
    }

    const termesNet = [ca, -r.cotisations, -r.cfp, -r.impot, ...(frais > 0 ? [-frais] : [])];
    const calculNet = [eur(ca, lang), eur(r.cotisations, lang), eur(r.cfp, lang), eur(r.impot, lang), ...(frais > 0 ? [eur(frais, lang)] : [])].join(" − ");
    const net = {
        etapes: [etape(frais > 0 ? T.netFrais : T.net, calculNet, r.net - frais, somme(...termesNet))],
        sources: [],
    };

    return { cotisations, cfp, impot: { etapes: etapesImpot, sources: sourcesImpot }, net, resultat: r };
}

/**
 * Les explications du tarif journalier : les jours facturés, le chiffre
 * d'affaires qu'il faut encaisser, et le tarif qui en découle.
 *
 * @param r le résultat de `tarifJournalier`
 */
export function expliquerTarif({ parametres, netMensuel, fraisMensuels = 0, joursParSemaine, semainesConges, annee }, r, lang = "fr") {
    const T = TEXTES[lang] || TEXTES.fr;
    const ouvres = joursOuvres(annee);
    const feries = joursFeriesEnSemaine(annee);
    const disponibles = ouvres - feries;
    const frais = fraisMensuels * 12;
    const micro = expliquerMicro({ ...parametres, chiffreAffaires: r.chiffreAffaires }, lang, { frais });

    return {
        ...micro,
        jours: {
            etapes: [
                etape(T.joursOuvres, `${ouvres} − ${feries}`, disponibles, somme(ouvres, -feries), "jours"),
                etape(
                    remplir(T.joursFactures, { jours: joursParSemaine, semaines: semainesConges }),
                    `${disponibles} × ${joursParSemaine} ÷ 5 − ${semainesConges} × ${joursParSemaine}`,
                    r.jours,
                    plancher(somme(quotient(produit(disponibles, joursParSemaine), 5), produit(-semainesConges, joursParSemaine))),
                    "jours",
                ),
            ],
            sources: ["jours_feries_legaux"],
        },
        frais: frais > 0 ? { etapes: [etape(T.frais, `${eur(fraisMensuels, lang)} × 12`, frais, produit(fraisMensuels, 12))], sources: [] } : null,
        chiffreAffaires: {
            etapes: [
                etape(remplir(T.chiffreAffaires, { objectif: eur(netMensuel, lang) }), T.euros, r.chiffreAffaires),
                ...micro.net.etapes,
            ],
            sources: [],
        },
        tarif: {
            etapes: [etape(T.tarif, `${eur(r.chiffreAffaires, lang)} ÷ ${r.jours}`, r.tarifJour, quotientArrondiSup(r.chiffreAffaires, r.jours))],
            sources: [],
        },
    };
}

/* ------------------------------------------------------------------------- */
/* Micro-entreprise : versement libératoire, ACRE, indemnités journalières,  */
/* trimestres de retraite, congé de naissance                                 */
/* ------------------------------------------------------------------------- */

const TEXTES_MICRO = {
    fr: {
        ...TEXTES.fr,
        impotBareme: "Impôt de votre foyer au barème (barème, parts, décote)",
        autresTauxEffectif: "Impôt du foyer au barème × vos autres revenus ÷ le revenu imposable du foyer",
        vflTotal: "Versement libératoire plus l'impôt de vos autres revenus",
        economieVfl: "Impôt au barème moins l'impôt avec l'option",
        surcoutVfl: "Impôt avec l'option moins l'impôt au barème",
        acreAvec: "Chiffre d'affaires × taux réduit de l'ACRE",
        acreSans: "Chiffre d'affaires × taux normal",
        acreEconomie: "Cotisations sans l'ACRE moins cotisations avec",
        ijRevenu: "Revenu retenu : chiffre d'affaires moyen moins l'abattement",
        ijRevenuMinimum: "Revenu retenu : chiffre d'affaires moyen moins l'abattement minimum",
        ijPlafond: "Revenu retenu dans la limite du plafond de la Sécurité sociale",
        ijParJour: "Revenu retenu ÷ 730",
        ijJours: "Jours d'arrêt moins {carence} jours de carence",
        ijTotal: "Indemnité par jour × jours indemnisés",
        retraiteCotisation: "Cotisations de retraite de base : chiffre d'affaires × taux global × part de la retraite de base",
        retraiteRevenu: "Revenu cotisé : ces cotisations ÷ 17,87 %, le taux de la cotisation de retraite de base",
        retraiteTrimestre: "Revenu qui valide un trimestre : 150 heures × SMIC horaire du 1er janvier",
        retraiteValides: "Revenu cotisé ÷ revenu d'un trimestre, arrondi au trimestre inférieur",
        auPlus: ", 4 trimestres au plus",
        cipavPaliers: "Paliers de chiffre d'affaires de la Cipav",
        cipavAtteints: "{ca} atteint {n} des 4 paliers",
        cipavQuatre: "Palier des quatre trimestres, publié par l'Urssaf",
        cipavTableau: "tableau de l'Urssaf pour 2026",
        allocation: "Allocation forfaitaire de repos maternel",
        allocationReduite: "Allocation forfaitaire de repos maternel, au dixième sous 4 582 € de revenu",
        allocationCalcul: "versée en deux fois",
        ijNaissance: "Indemnité journalière forfaitaire × jours indemnisés",
        ijTropCourt: "Arrêt de {jours} jours, sous les 56 jours minimum : aucune indemnité",
        totalNaissance: "Allocation plus indemnités journalières",
        csnForfait: "Indemnité journalière forfaitaire : plafond de la Sécurité sociale ÷ 730",
        csnPremier: "Premier mois du congé supplémentaire : 70 % de cette indemnité",
        csnSecond: "Second mois : 60 % de cette indemnité",
        csnReduit: "Sous le plancher de revenu : 10 % de 1/730 du plafond, les deux mois",
    },
    en: {
        ...TEXTES.en,
        impotBareme: "Your household's tax on the scale (scale, shares, reduction)",
        autresTauxEffectif: "Household tax on the scale × your other income ÷ the household's taxable income",
        vflTotal: "Flat-rate tax payment plus the tax on your other income",
        economieVfl: "Tax on the scale minus tax with the option",
        surcoutVfl: "Tax with the option minus tax on the scale",
        acreAvec: "Turnover × reduced ACRE rate",
        acreSans: "Turnover × standard rate",
        acreEconomie: "Contributions without ACRE minus contributions with it",
        ijRevenu: "Income taken into account: average turnover minus the allowance",
        ijRevenuMinimum: "Income taken into account: average turnover minus the minimum allowance",
        ijPlafond: "Income taken into account, up to the social security ceiling",
        ijParJour: "Income taken into account ÷ 730",
        ijJours: "Days off work minus {carence} waiting days",
        ijTotal: "Benefit per day × days paid",
        retraiteCotisation: "Basic pension contributions: turnover × overall rate × basic pension share",
        retraiteRevenu: "Credited income: those contributions ÷ 17.87%, the basic pension contribution rate",
        retraiteTrimestre: "Income that validates a quarter: 150 hours × hourly minimum wage on 1 January",
        retraiteValides: "Credited income ÷ income for one quarter, rounded down",
        auPlus: ", 4 quarters at most",
        cipavPaliers: "Cipav turnover thresholds",
        cipavAtteints: "{ca} reaches {n} of the 4 thresholds",
        cipavQuatre: "Threshold for four quarters, published by the Urssaf",
        cipavTableau: "Urssaf table for 2026",
        allocation: "Flat-rate maternity rest allowance",
        allocationReduite: "Flat-rate maternity rest allowance, at one tenth below €4,582 of income",
        allocationCalcul: "paid in two instalments",
        ijNaissance: "Flat-rate daily benefit × days paid",
        ijTropCourt: "{jours} days off, below the 56-day minimum: no benefit",
        totalNaissance: "Allowance plus daily benefits",
        csnForfait: "Flat-rate daily benefit: social security ceiling ÷ 730",
        csnPremier: "First month of the supplementary leave: 70% of that benefit",
        csnSecond: "Second month: 60% of that benefit",
        csnReduit: "Below the income floor: 10% of 1/730 of the ceiling, both months",
    },
};

/**
 * Le versement libératoire : l'impôt du foyer au barème, l'impôt avec
 * l'option, et l'écart entre les deux, dans le sens du gagnant.
 *
 * @param p les paramètres de `comparerVersementLiberatoire`, activité fiscale
 */
export function expliquerVersementLiberatoire(p, lang = "fr") {
    const T = TEXTES_MICRO[lang] || TEXTES_MICRO.fr;
    const r = comparerVersementLiberatoire(p);
    const ca = p.chiffreAffaires;
    const autres = p.autresRevenus || 0;
    const foyer = { enCouple: p.enCouple, enfants: p.enfants || 0, parentIsole: p.parentIsole };

    const benefice = etapeBenefice(ca, p.activite, lang, T);
    const etapesBareme = [benefice.etape];
    if (autres > 0) etapesBareme.push(etape(T.revenuFoyer, `${eur(r.benefice, lang)} + ${eur(autres, lang)}`, r.revenuTotal, somme(r.benefice, autres)));
    etapesBareme.push(etape(T.impotBareme, remplir(T.sur, { montant: eur(r.revenuTotal, lang) }), r.impotBareme));
    const bareme = { etapes: etapesBareme, sources: [...benefice.sources, ...sourcesImpotFoyer(r.revenuTotal, foyer)] };

    const taux = ACTIVITES[p.activite].vfl;
    const etapesVfl = [etape(T.vfl, `${eur(ca, lang)} × ${pct(taux, lang)}`, r.vflSurCa, produit(ca, taux))];
    const sourcesVfl = ["vfl_taux"];
    if (autres > 0) {
        etapesVfl.push(
            etape(
                T.autresTauxEffectif,
                `${eur(r.impotBareme, lang)} × ${eur(autres, lang)} ÷ ${eur(r.revenuTotal, lang)}`,
                r.impotAutresRevenusEnVfl,
                quotient(produit(r.impotBareme, autres), r.revenuTotal),
            ),
        );
        etapesVfl.push(etape(T.vflTotal, `${eur(r.vflSurCa, lang)} + ${eur(r.impotAutresRevenusEnVfl, lang)}`, r.impotVfl, somme(r.vflSurCa, r.impotAutresRevenusEnVfl)));
        sourcesVfl.push("vfl_regle_taux_effectif", ...sourcesImpotFoyer(r.revenuTotal, foyer));
    }
    const vfl = { etapes: etapesVfl, sources: sourcesVfl };

    const gagneVfl = r.ecart > 0;
    const ecart = {
        etapes: [
            gagneVfl
                ? etape(T.economieVfl, `${eur(r.impotBareme, lang)} − ${eur(r.impotVfl, lang)}`, r.ecart, somme(r.impotBareme, -r.impotVfl))
                : etape(T.surcoutVfl, `${eur(r.impotVfl, lang)} − ${eur(r.impotBareme, lang)}`, -r.ecart, somme(r.impotVfl, -r.impotBareme)),
        ],
        sources: [],
    };

    return { bareme, vfl, ecart, resultat: r };
}

const SOURCE_ACRE = { avant: "acre_taux_micro_avant_010726", apres: "acre_taux_micro_apres_010726" };

/**
 * L'ACRE : les cotisations avec l'aide, sans elle, et ce qu'elle fait gagner.
 *
 * @param r le résultat de `economieAcre`
 */
export function expliquerAcre({ chiffreAffaires, activite }, r, lang = "fr") {
    const T = TEXTES_MICRO[lang] || TEXTES_MICRO.fr;
    return {
        avec: {
            etapes: [etape(T.acreAvec, `${eur(chiffreAffaires, lang)} × ${pct(r.tauxAide, lang)}`, r.avecAide, produit(chiffreAffaires, r.tauxAide))],
            sources: [SOURCE_ACRE[r.regime]],
        },
        sans: {
            etapes: [etape(T.acreSans, `${eur(chiffreAffaires, lang)} × ${pct(r.tauxPlein, lang)}`, r.sansAide, produit(chiffreAffaires, r.tauxPlein))],
            sources: [SOURCE_TAUX[activite]],
        },
        economie: {
            etapes: [etape(T.acreEconomie, `${eur(r.sansAide, lang)} − ${eur(r.avecAide, lang)}`, r.economie, somme(r.sansAide, -r.avecAide))],
            sources: [SOURCE_ACRE[r.regime], "acre_duree_micro"],
        },
    };
}

/**
 * Les indemnités journalières d'un micro-entrepreneur : le revenu retenu,
 * l'indemnité par jour, et le total de l'arrêt.
 *
 * @param r le résultat de `indemniteJournaliere`
 */
export function expliquerIndemnitesJournalieres({ chiffreAffairesMoyen, activite }, r, lang = "fr") {
    const T = TEXTES_MICRO[lang] || TEXTES_MICRO.fr;
    if (!r.ouvre) return { parJour: null, total: null };
    const benefice = etapeBenefice(chiffreAffairesMoyen, activite, lang, { benefice: T.ijRevenu, beneficeMinimum: T.ijRevenuMinimum });
    const etapesJour = [benefice.etape];
    const sourcesJour = [...benefice.sources, "ij_revenu_minimum", "ij_formule"];
    let retenu = r.revenuAnnuelMoyen;
    if (r.plafonne) {
        retenu = IJ.revenuPlafond;
        etapesJour.push(etape(T.ijPlafond, plusPetit(eur(r.revenuAnnuelMoyen, lang), eur(IJ.revenuPlafond, lang), lang), retenu, minimum(r.revenuAnnuelMoyen, IJ.revenuPlafond)));
        sourcesJour.push("ij_plafond_revenu", "ij_montant_maximal");
    }
    etapesJour.push(etape(T.ijParJour, `${eur(retenu, lang)} ÷ ${IJ.diviseur}`, r.montant, quotient(retenu, IJ.diviseur), "centimes"));
    const parJour = { etapes: etapesJour, sources: sourcesJour };

    const total = {
        etapes: [
            etape(remplir(T.ijJours, { carence: IJ.carence }), `${r.jours} − ${IJ.carence}`, r.joursIndemnises, plancherZero(somme(r.jours, -IJ.carence)), "jours"),
            etape(T.ijTotal, `${eurCentimes(r.montant, lang)} × ${r.joursIndemnises}`, r.total, produit(r.montant, r.joursIndemnises)),
        ],
        sources: ["ij_carence"],
    };
    return { parJour, total };
}

const SOURCE_REPARTITION = { vente: "repartition_2026_bic", services: "repartition_2026_bic", bnc: "repartition_2026_bnc" };

/**
 * Les trimestres de retraite d'une année, par la méthode de Service-Public
 * (F23369) : les cotisations de retraite de base, reconverties en revenu
 * cotisé par le taux de 17,87 %, puis comptées par tranches de 150 heures de
 * SMIC. Ou les paliers de la Cipav, publiés en chiffre d'affaires.
 *
 * @param r le résultat de `trimestresRetraite`
 */
export function expliquerTrimestres({ chiffreAffaires, activite }, r, lang = "fr") {
    const T = TEXTES_MICRO[lang] || TEXTES_MICRO.fr;
    if (r.regle === "cipav") {
        const paliers = PALIERS_CIPAV.map((x) => eur(x, lang)).join(", ");
        return {
            valides: {
                etapes: [
                    etape(
                        `${T.cipavPaliers}${lang === "fr" ? "\u00A0: " : ": "}${paliers}`,
                        remplir(T.cipavAtteints, { ca: eur(chiffreAffaires, lang), n: r.valides }),
                        r.valides,
                        MOTEUR,
                        "trimestres",
                    ),
                ],
                sources: ["retraite_cipav_trimestres_2026"],
            },
            revenu: { etapes: [etape(T.cipavQuatre, T.cipavTableau, r.caQuatreTrimestres)], sources: ["retraite_cipav_trimestres_2026"] },
        };
    }
    const taux = TAUX_COTISATIONS[activite];
    const part = PART_RETRAITE_BASE[activite];
    const cotisation = chiffreAffaires * taux * part;
    const etapesRevenu = [
        etape(T.retraiteCotisation, `${eur(chiffreAffaires, lang)} × ${pct(taux, lang)} × ${pctPrecis(part, lang)}`, cotisation, produit(chiffreAffaires, taux, part), "centimes"),
        etape(T.retraiteRevenu, `${eurCentimes(cotisation, lang)} ÷ ${pctPrecis(TAUX_RETRAITE_BASE, lang)}`, r.revenuRetenu, quotient(cotisation, TAUX_RETRAITE_BASE)),
    ];
    const sourcesRevenu = [SOURCE_TAUX[activite], SOURCE_REPARTITION[activite], "retraite_revenu_cotise_reconstitution"];
    return {
        valides: {
            etapes: [
                ...etapesRevenu,
                etape(
                    T.retraiteTrimestre,
                    `${HEURES_PAR_TRIMESTRE} × ${eurCentimes(SMIC_HORAIRE_REFERENCE, lang)}`,
                    REVENU_PAR_TRIMESTRE,
                    produit(HEURES_PAR_TRIMESTRE, SMIC_HORAIRE_REFERENCE),
                ),
                etape(
                    T.retraiteValides,
                    `${eur(r.revenuRetenu, lang)} ÷ ${eur(REVENU_PAR_TRIMESTRE, lang)}${T.auPlus}`,
                    r.valides,
                    minimum(4, plancher(quotient(r.revenuRetenu, REVENU_PAR_TRIMESTRE))),
                    "trimestres",
                ),
            ],
            sources: [...sourcesRevenu, "retraite_regle_150h_smic", "smic_horaire_2026"],
        },
        revenu: { etapes: etapesRevenu, sources: sourcesRevenu },
    };
}

/**
 * Le congé de naissance d'une indépendante ou d'un indépendant : l'allocation,
 * les indemnités journalières, et leur total.
 *
 * @param droits le résultat de `droitsMaternite` ou de `droitsPaternite`
 */
export function expliquerCongeNaissance(droits, lang = "fr") {
    const T = TEXTES_MICRO[lang] || TEXTES_MICRO.fr;
    if (!droits) return { total: null };
    const maternite = droits.allocation > 0;
    const etapes = [];
    const sources = [];
    if (maternite) {
        etapes.push(etape(droits.reduit ? T.allocationReduite : T.allocation, T.allocationCalcul, droits.allocation, MOTEUR, "centimes"));
        sources.push(droits.reduit ? "maternite_allocation_repos_reduite" : "maternite_allocation_repos");
    }
    if (droits.joursIndemnises > 0) {
        etapes.push(
            etape(T.ijNaissance, `${eurCentimes(droits.journaliere, lang, 3)} × ${droits.joursIndemnises}`, droits.ij, produit(droits.journaliere, droits.joursIndemnises), "centimes"),
        );
    } else if (maternite) {
        etapes.push(etape(remplir(T.ijTropCourt, { jours: droits.joursArret }), eurCentimes(0, lang), 0, MOTEUR, "centimes"));
    }
    sources.push(maternite ? "maternite_ij_forfaitaire" : "paternite_duree");
    if (maternite) sources.push("maternite_arret_minimal");
    if (droits.reduit) sources.push("ij_revenu_minimum");
    if (maternite) {
        etapes.push(etape(T.totalNaissance, `${eurCentimes(droits.allocation, lang)} + ${eurCentimes(droits.ij, lang)}`, droits.total, somme(droits.allocation, droits.ij), "centimes"));
    }
    return { total: { etapes, sources } };
}

/**
 * Le congé supplémentaire de naissance : l'indemnité par jour du premier et
 * du second mois, depuis l'indemnité forfaitaire.
 *
 * @param c le résultat de `congeSupplementaire`
 */
export function expliquerCongeSupplementaire(c, lang = "fr") {
    const T = TEXTES_MICRO[lang] || TEXTES_MICRO.fr;
    if (!c?.ouvert) return { premier: null, second: null };
    const sources = ["conge_supplementaire_naissance", "pass_2026"];
    if (c.reduit) {
        const reduit = {
            etapes: [
                etape(
                    T.csnReduit,
                    `${eur(PASS_NAISSANCE, lang)} ÷ 730 × ${pct(0.1, lang)}`,
                    c.parJour[0],
                    produit(quotient(PASS_NAISSANCE, 730), 0.1),
                    "centimes",
                ),
            ],
            sources,
        };
        return { premier: reduit, second: reduit };
    }
    const forfait = etape(T.csnForfait, `${eur(PASS_NAISSANCE, lang)} ÷ 730`, IJ_FORFAITAIRE, quotient(PASS_NAISSANCE, 730), "centimes");
    const [taux1, taux2] = CONGE_SUPPLEMENTAIRE.coefficients;
    return {
        premier: {
            etapes: [forfait, etape(T.csnPremier, `${eurCentimes(IJ_FORFAITAIRE, lang)} × ${pct(taux1, lang)}`, c.parJour[0], produit(IJ_FORFAITAIRE, taux1), "centimes")],
            sources,
        },
        second: {
            etapes: [forfait, etape(T.csnSecond, `${eurCentimes(IJ_FORFAITAIRE, lang)} × ${pct(taux2, lang)}`, c.parJour[1], produit(IJ_FORFAITAIRE, taux2), "centimes")],
            sources,
        },
    };
}

/* ------------------------------------------------------------------------- */
/* Holding                                                                    */
/* ------------------------------------------------------------------------- */

const TEXTES_HOLDING = {
    fr: {
        is: "Impôt sur les sociétés de votre société",
        dividendes: "Bénéfice moins l'impôt sur les sociétés",
        ir: "Dividendes × 12,8 %",
        ps: "Part soumise aux prélèvements sociaux × 18,6 %",
        psEurl: "Dividendes jusqu'à 10 % du capital, × 18,6 %",
        cotisations: "Cotisations sur votre rémunération et les dividendes au-delà de 10 % du capital, moins celles de votre rémunération seule",
        surCompte: "Dividendes moins l'impôt, les prélèvements et les cotisations",
        quotePart: "Dividendes × 5 %, la seule part imposée",
        impotHolding: "Impôt sur les sociétés de la holding sur cette part",
        dansHolding: "Dividendes moins l'impôt de la holding",
        reverse: "Ce qui reste dans la holding × 31,4 %",
        via: "Ce qui reste dans la holding moins les 31,4 %",
        plusValue: "Prix de vente moins le prix payé pour vos parts",
        impotVente: "Plus-value × 31,4 %",
        restePrix: "Prix de vente moins l'impôt",
        plusValueHolding: "Prix de vente moins la valeur des parts à leur entrée dans la holding",
        quotePartVente: "Plus-value × 12 %, la seule part imposée",
        impotHoldingVente: "Impôt sur les sociétés sur cette part",
        impotCourt: "Moins de deux ans : toute la plus-value paie l'impôt sur les sociétés",
    },
    en: {
        is: "Your company's corporate tax",
        dividendes: "Profit minus corporate tax",
        ir: "Dividends × 12.8%",
        ps: "Share subject to social levies × 18.6%",
        psEurl: "Dividends up to 10% of the capital, × 18.6%",
        cotisations: "Contributions on your pay plus the dividends above 10% of the capital, minus those on your pay alone",
        surCompte: "Dividends minus tax, levies and contributions",
        quotePart: "Dividends × 5%, the only part taxed",
        impotHolding: "The holding's corporate tax on that part",
        dansHolding: "Dividends minus the holding's tax",
        reverse: "What stays in the holding × 31.4%",
        via: "What stays in the holding minus the 31.4%",
        plusValue: "Sale price minus what you paid for your shares",
        impotVente: "Capital gain × 31.4%",
        restePrix: "Sale price minus the tax",
        plusValueHolding: "Sale price minus the value of the shares when they entered the holding",
        quotePartVente: "Capital gain × 12%, the only part taxed",
        impotHoldingVente: "Corporate tax on that part",
        impotCourt: "Less than two years: the whole gain bears corporate tax",
    },
};

/**
 * Les explications de la colonne « sans holding » et de la colonne « avec »,
 * pour vivre de l'argent ou le réinvestir.
 */
export function expliquerHoldingDividendes(
    { forme, benefice, capital = 1000, enveloppeRemuneration = 0, activite = "services", artisan = false },
    lang = "fr",
) {
    const T = TEXTES_HOLDING[lang] || TEXTES_HOLDING.fr;
    const r = reinvestir({ forme, benefice, capital, enveloppeRemuneration, activite, artisan });
    const d = r.dividendes;
    const base = forme === "eurl" ? Math.min(d, SEUIL_DIVIDENDES_EURL * capital) : d;
    const cotisant = d - base;

    const dividendes = {
        etapes: [etapeIs(T.is, benefice, lang), etape(T.dividendes, `${eur(benefice, lang)} − ${eur(r.impotSociete, lang)}`, d, somme(benefice, -r.impotSociete))],
        sources: ["is_taux"],
    };
    const impotRevenu = { etapes: [etape(T.ir, `${eur(d, lang)} × ${pct(PFU_IR, lang)}`, r.sans.impot, produit(d, PFU_IR))], sources: ["pfu"] };
    const prelevements = {
        etapes: [etape(forme === "eurl" ? T.psEurl : T.ps, `${eur(base, lang)} × ${pct(PFU_SOCIAL, lang)}`, r.sans.social, produit(base, PFU_SOCIAL))],
        sources: forme === "eurl" ? ["pfu", "regle_10_pct_eurl"] : ["pfu"],
    };
    const avant = cotisationsIndependant({ revenuAvantCotisations: enveloppeRemuneration, activite, artisan }).total;
    const apres = cotisationsIndependant({ revenuAvantCotisations: enveloppeRemuneration + cotisant, activite, artisan }).total;
    const cotisations =
        forme === "eurl"
            ? {
                  etapes: [etape(T.cotisations, `${eur(apres, lang)} − ${eur(avant, lang)}`, r.sans.cotisations, plancherZero(somme(apres, -avant)))],
                  sources: ["regle_10_pct_eurl", "cotisations_independants"],
              }
            : null;
    const surCompte = {
        etapes: [
            etape(
                T.surCompte,
                [eur(d, lang), eur(r.sans.impot, lang), eur(r.sans.social, lang), ...(forme === "eurl" ? [eur(r.sans.cotisations, lang)] : [])].join(" − "),
                r.sans.net,
                somme(d, -r.sans.impot, -r.sans.social, -r.sans.cotisations),
            ),
        ],
        sources: [],
    };
    const quotePart = d * QUOTE_PART_MERE_FILLE;
    const impotHolding = {
        etapes: [
            etape(T.quotePart, `${eur(d, lang)} × ${pct(QUOTE_PART_MERE_FILLE, lang)}`, r.avec.quotePart, produit(d, QUOTE_PART_MERE_FILLE)),
            etapeIs(T.impotHolding, quotePart, lang),
        ],
        sources: ["quote_part_mere_fille", "is_taux"],
    };
    const dansHolding = { etapes: [etape(T.dansHolding, `${eur(d, lang)} − ${eur(r.avec.impot, lang)}`, r.avec.net, somme(d, -r.avec.impot))], sources: [] };
    const reverseMontant = r.avec.net - r.vivre.net;
    const reverse = {
        etapes: [etape(T.reverse, `${eur(r.avec.net, lang)} × ${pct(PFU_IR + PFU_SOCIAL, lang)}`, reverseMontant, produit(r.avec.net, PFU_IR + PFU_SOCIAL))],
        sources: ["pfu"],
    };
    const via = { etapes: [etape(T.via, `${eur(r.avec.net, lang)} − ${eur(reverseMontant, lang)}`, r.vivre.net, somme(r.avec.net, -reverseMontant))], sources: [] };

    return { dividendes, impotRevenu, prelevements, cotisations, surCompte, impotHolding, dansHolding, reverse, via, resultat: r };
}

/** Les explications de la vente, avec ou sans holding. */
export function expliquerHoldingVente({ prix, acquisition = 0, valeurApport = null, deuxAns = true }, lang = "fr") {
    const T = TEXTES_HOLDING[lang] || TEXTES_HOLDING.fr;
    const v = revendre({ prix, acquisition, valeurApport, deuxAns });
    const base = valeurApport === null ? acquisition : valeurApport;
    const impotVente = {
        etapes: [
            etape(T.plusValue, `${eur(prix, lang)} − ${eur(acquisition, lang)}`, v.plusValue, plancherZero(somme(prix, -acquisition))),
            etape(T.impotVente, `${eur(v.plusValue, lang)} × ${pct(PFU_IR + PFU_SOCIAL, lang)}`, v.sans.impot, produit(v.plusValue, PFU_IR + PFU_SOCIAL)),
        ],
        sources: ["pfu_plus_values"],
    };
    const resteSans = { etapes: [etape(T.restePrix, `${eur(prix, lang)} − ${eur(v.sans.impot, lang)}`, v.sans.net, somme(prix, -v.sans.impot))], sources: [] };
    const etapesHolding = [etape(T.plusValueHolding, `${eur(prix, lang)} − ${eur(base, lang)}`, v.avec.plusValue, plancherZero(somme(prix, -base)))];
    if (deuxAns) {
        etapesHolding.push(etape(T.quotePartVente, `${eur(v.avec.plusValue, lang)} × ${pct(QUOTE_PART_PLUS_VALUE, lang)}`, v.avec.quotePart, produit(v.avec.plusValue, QUOTE_PART_PLUS_VALUE)));
        etapesHolding.push(etapeIs(T.impotHoldingVente, v.avec.quotePart, lang));
    } else {
        etapesHolding.push(etapeIs(T.impotCourt, v.avec.plusValue, lang));
    }
    const impotHoldingVente = { etapes: etapesHolding, sources: deuxAns ? ["quote_part_plus_value", "is_taux"] : ["is_taux"] };
    const dansHolding = { etapes: [etape(T.restePrix, `${eur(prix, lang)} − ${eur(v.avec.impot, lang)}`, v.avec.net, somme(prix, -v.avec.impot))], sources: [] };
    return { impotVente, resteSans, impotHoldingVente, dansHolding, resultat: v };
}

/* ------------------------------------------------------------------------- */
/* Sociétés : rémunération, cotisations, dividendes, impôt du dirigeant       */
/* ------------------------------------------------------------------------- */

/**
 * Les sources des sociétés, par rôle. Un seul endroit à changer si une clé du
 * catalogue (lib/tools/sources.js) change de nom.
 */
const S = {
    is: "is_taux",
    pfu: "pfu",
    dixPourcent: "regle_10_pct_eurl",
    independants: "cotisations_independants",
    cfpIndependants: "cfp_independants",
    assimile: "taux_assimile_salarie",
    fraisPro: "abattement_frais_professionnels",
    abattementDividendes: "abattement_dividendes_bareme",
    kilometrique: "bareme_kilometrique",
    ijSalarie: "ij_societe_salarie",
    smic: "smic_horaire_2026",
    pass: "pass_2026",
    ijFormule: "ij_formule",
    ijMinimum: "ij_revenu_minimum",
    ijPlafond: "ij_plafond_revenu",
    ijCarence: "ij_carence",
    ijLiberalReglemente: "ij_liberal_reglemente",
    csgIj: "csg_crds_ij",
};

const TEXTES_SOCIETE = {
    fr: {
        part: "Part de l'enveloppe consacrée à la rémunération",
        patronales: "Cotisations patronales d'un assimilé salarié",
        salariales: "Cotisations salariales d'un assimilé salarié",
        surBrut: "sur {montant} de salaire brut",
        brutSasu: "Salaire brut : cette part, une fois payées les cotisations patronales",
        netSasu: "Salaire net : brut moins cotisations salariales",
        cotisationsSasu: "Cotisations patronales plus cotisations salariales",
        cotisationsTns: "Cotisations d'indépendant sur cette part",
        surRevenu: "sur {montant} de revenu",
        remunerationEurl: "Rémunération : cette part moins les cotisations",
        assiette: "Assiette sociale : revenu moins l'abattement de 26 % (encadré)",
        maladie: "Maladie-maternité, au taux progressif",
        ij: "Indemnités journalières",
        vieillesse: "Retraite de base",
        complementaire: "Retraite complémentaire",
        invalidite: "Invalidité-décès",
        allocations: "Allocations familiales, au taux progressif",
        csg: "CSG-CRDS : assiette × 9,7 %",
        formation: "Formation professionnelle : forfait sur le plafond de la Sécurité sociale",
        surAssiette: "sur {montant} d'assiette",
        totalTns: "Total des cotisations d'indépendant",
        resultat: "Bénéfice de la société : enveloppe moins le coût de la rémunération",
        is: "Impôt sur les sociétés",
        dividendes: "Dividendes : bénéfice moins l'impôt sur les sociétés",
        cotisants: "Dividendes au-delà de 10 % du capital",
        cotisantsBenefice: "Dividendes au-delà de 10 % du bénéfice distribué",
        dixPourcent: "{dividendes} − 10 % × {capital}",
        cotisationsAvec: "Cotisations sur la rémunération et ces dividendes",
        cotisationsSans: "Cotisations sur la rémunération seule",
        cotisationsDividendes: "Ce que ces dividendes ajoutent aux cotisations",
        aucunCotisant: "Dividendes sous 10 % du capital : aucune cotisation",
        prelevements: "Prélèvements sociaux : dividendes × 18,6 %",
        prelevementsEurl: "Prélèvements sociaux : dividendes jusqu'à 10 % du capital, × 18,6 %",
        fraisPro: "Abattement pour frais professionnels : 10 %, 509 € au moins, 14 555 € au plus",
        imposable: "Rémunération imposable : net plus CSG non déductible, moins l'abattement",
        imposableBareme: "Revenu imposable : rémunération imposable plus 60 % des dividendes (abattement de 40 %)",
        imposableDividendes: "Revenu imposable : 60 % des dividendes (abattement de 40 %)",
        impotSeul: "Impôt de votre foyer sur ce revenu (barème, parts, décote)",
        impotAvec: "Impôt de votre foyer avec ce revenu",
        impotSans: "Impôt de votre foyer sans lui",
        impotAjoute: "Ce que ce revenu ajoute à l'impôt du foyer",
        sur: "sur {montant} de revenu imposable",
        surAutres: "sur vos {montant} d'autres revenus",
        pfuIr: "Dividendes × 12,8 %",
        impotTotal: "Impôt sur la rémunération plus impôt sur les dividendes",
        reste: "Ce qui arrive sur votre compte : rémunération nette et dividendes, moins prélèvements et impôt",
        ik: "{kilometres} km × {coefficient} €",
        ikFixe: "{kilometres} km × {coefficient} € + {fixe} €",
        ikLibelle: "Barème kilométrique, tranche {tranche}",
        tranches: ["jusqu'à {bas} km", "de {basPlusUn} à {haut} km", "au-delà de {haut} km"],
        retenuSasu: "Salaire retenu : salaire brut mensuel, 1,4 SMIC au plus ({plafond})",
        parJourSasu: "Indemnité par jour : 50 % de trois mois de salaire ÷ 91,25",
        retenuEurl: "Revenu retenu : revenu annuel moyen, {plafond} au plus",
        parJourEurl: "Indemnité par jour : revenu retenu ÷ 730",
        joursCarence: "Jours d'arrêt moins {carence} jours de carence",
        joursReglemente: "Jours d'arrêt moins {carence} jours de carence, 87 jours au plus",
        totalArret: "Indemnité par jour × jours indemnisés",
        netArret: "Total moins la CSG et la CRDS de 6,7 %",
    },
    en: {
        part: "Share of the envelope set aside for pay",
        patronales: "Employer contributions of an assimilated employee",
        salariales: "Employee contributions of an assimilated employee",
        surBrut: "on {montant} of gross salary",
        brutSasu: "Gross salary: that share, once employer contributions are paid",
        netSasu: "Net salary: gross minus employee contributions",
        cotisationsSasu: "Employer contributions plus employee contributions",
        cotisationsTns: "Self-employed contributions on that share",
        surRevenu: "on {montant} of income",
        remunerationEurl: "Pay: that share minus contributions",
        assiette: "Contribution base: income minus the 26% allowance (capped)",
        maladie: "Health and maternity, at the sliding rate",
        ij: "Daily sickness benefits",
        vieillesse: "Basic pension",
        complementaire: "Supplementary pension",
        invalidite: "Disability and death",
        allocations: "Family allowances, at the sliding rate",
        csg: "CSG-CRDS: base × 9.7%",
        formation: "Training contribution: flat rate on the social security ceiling",
        surAssiette: "on a base of {montant}",
        totalTns: "Total self-employed contributions",
        resultat: "Company profit: envelope minus the cost of pay",
        is: "Corporate tax",
        dividendes: "Dividends: profit minus corporate tax",
        cotisants: "Dividends above 10% of the capital",
        cotisantsBenefice: "Dividends above 10% of the profit paid out",
        dixPourcent: "{dividendes} − 10% × {capital}",
        cotisationsAvec: "Contributions on pay plus those dividends",
        cotisationsSans: "Contributions on pay alone",
        cotisationsDividendes: "What those dividends add to contributions",
        aucunCotisant: "Dividends below 10% of the capital: no contributions",
        prelevements: "Social levies: dividends × 18.6%",
        prelevementsEurl: "Social levies: dividends up to 10% of the capital, × 18.6%",
        fraisPro: "Allowance for professional expenses: 10%, at least €509, at most €14,555",
        imposable: "Taxable pay: net plus non-deductible CSG, minus the allowance",
        imposableBareme: "Taxable income: taxable pay plus 60% of dividends (40% allowance)",
        imposableDividendes: "Taxable income: 60% of dividends (40% allowance)",
        impotSeul: "Your household's tax on that income (scale, shares, reduction)",
        impotAvec: "Your household's tax with that income",
        impotSans: "Your household's tax without it",
        impotAjoute: "What that income adds to the household's tax",
        sur: "on {montant} of taxable income",
        surAutres: "on your {montant} of other income",
        pfuIr: "Dividends × 12.8%",
        impotTotal: "Tax on pay plus tax on dividends",
        reste: "What reaches your account: net pay and dividends, minus levies and tax",
        ik: "{kilometres} km × €{coefficient}",
        ikFixe: "{kilometres} km × €{coefficient} + €{fixe}",
        ikLibelle: "Mileage scale, band {tranche}",
        tranches: ["up to {bas} km", "{basPlusUn} to {haut} km", "over {haut} km"],
        retenuSasu: "Salary taken into account: monthly gross salary, 1.4 × minimum wage at most ({plafond})",
        parJourSasu: "Benefit per day: 50% of three months' salary ÷ 91.25",
        retenuEurl: "Income taken into account: average annual income, {plafond} at most",
        parJourEurl: "Benefit per day: income taken into account ÷ 730",
        joursCarence: "Days off work minus {carence} waiting days",
        joursReglemente: "Days off work minus {carence} waiting days, 87 days at most",
        totalArret: "Benefit per day × days paid",
        netArret: "Total minus 6.7% CSG and CRDS",
    },
};

/** Les cotisations d'un indépendant, ligne par ligne, jusqu'à leur total. */
function etapesCotisationsIndependant(revenu, activite, artisan, lang, T) {
    const c = cotisationsIndependant({ revenuAvantCotisations: revenu, activite, artisan });
    const base = Math.max(0, revenu);
    const sur = remplir(T.surAssiette, { montant: eur(c.assiette, lang) });
    const lignes = [
        etape(T.maladie, sur, c.maladie),
        etape(T.ij, sur, c.ij),
        etape(T.vieillesse, sur, c.vieillesseBase),
        etape(T.complementaire, sur, c.complementaire),
        etape(T.invalidite, sur, c.invalidite),
        etape(T.allocations, sur, c.allocations),
        etape(T.csg, `${eur(c.assiette, lang)} × ${pct(TAUX_CSG_CRDS, lang)}`, c.csgCrds, produit(c.assiette, TAUX_CSG_CRDS)),
        etape(
            T.formation,
            `${eur(PASS_2026, lang)} × ${pctPrecis(TAUX_CFP_INDEPENDANT[familleTns(activite, artisan)], lang)}`,
            c.formation,
            produit(PASS_2026, TAUX_CFP_INDEPENDANT[familleTns(activite, artisan)]),
            "centimes",
        ),
    ];
    const valeurs = [c.maladie, c.ij, c.vieillesseBase, c.complementaire, c.invalidite, c.allocations, c.csgCrds, c.formation];
    return {
        c,
        etapes: [
            etape(T.assiette, `${eur(base, lang)} − ${eur(base - c.assiette, lang)}`, c.assiette, somme(base, -(base - c.assiette))),
            ...lignes,
            etape(T.totalTns, valeurs.map((v) => eur(v, lang)).join(" + "), c.total, somme(...valeurs)),
        ],
        sources: [S.independants, S.pass, S.cfpIndependants],
    };
}

/**
 * Une rémunération imposable : le net plus la CSG non déductible, moins
 * l'abattement de 10 % pour frais, écrit avec la borne qui joue quand il en
 * joue une (509 € au moins, 14 555 € au plus, jamais plus que la rémunération).
 */
function etapesImposable(net, nonDeductible, lang, T) {
    const base = net + nonDeductible;
    const abattement = abattementFraisProfessionnels(base);
    const dix = `${eur(base, lang)} × ${pct(ABATTEMENT_FRAIS_PROFESSIONNELS, lang)}`;
    let etapeAbattement;
    if (base < ABATTEMENT_FRAIS_MINIMUM) {
        etapeAbattement = etape(T.fraisPro, plusPetit(eur(base, lang), eur(ABATTEMENT_FRAIS_MINIMUM, lang), lang), abattement, minimum(base, ABATTEMENT_FRAIS_MINIMUM));
    } else if (base * ABATTEMENT_FRAIS_PROFESSIONNELS < ABATTEMENT_FRAIS_MINIMUM) {
        etapeAbattement = etape(T.fraisPro, plusGrand(dix, eur(ABATTEMENT_FRAIS_MINIMUM, lang), lang), abattement, maximum(produit(base, ABATTEMENT_FRAIS_PROFESSIONNELS), ABATTEMENT_FRAIS_MINIMUM));
    } else if (base * ABATTEMENT_FRAIS_PROFESSIONNELS > ABATTEMENT_FRAIS_MAXIMUM) {
        etapeAbattement = etape(T.fraisPro, plusPetit(dix, eur(ABATTEMENT_FRAIS_MAXIMUM, lang), lang), abattement, minimum(produit(base, ABATTEMENT_FRAIS_PROFESSIONNELS), ABATTEMENT_FRAIS_MAXIMUM));
    } else {
        etapeAbattement = etape(T.fraisPro, dix, abattement, produit(base, ABATTEMENT_FRAIS_PROFESSIONNELS));
    }
    return [
        etapeAbattement,
        etape(T.imposable, `${eur(net, lang)} + ${eur(nonDeductible, lang)} − ${eur(abattement, lang)}`, base - abattement, somme(net, nonDeductible, -abattement)),
    ];
}

/** L'impôt qu'un revenu ajoute à celui du foyer : seul, ou avec ses autres revenus. */
function etapesImpotAjoute(imposable, { autresRevenus = 0, ...foyer }, lang, T) {
    const avec = impotSurLeRevenu(Math.max(0, autresRevenus + imposable), foyer);
    if (autresRevenus <= 0) {
        return { etapes: [etape(T.impotSeul, remplir(T.sur, { montant: eur(imposable, lang) }), avec)], valeur: avec, sources: sourcesImpotFoyer(imposable, foyer) };
    }
    const sans = impotSurLeRevenu(autresRevenus, foyer);
    const valeur = Math.max(0, avec - sans);
    return {
        etapes: [
            etape(T.impotAvec, remplir(T.sur, { montant: eur(autresRevenus + imposable, lang) }), avec),
            etape(T.impotSans, remplir(T.surAutres, { montant: eur(autresRevenus, lang) }), sans),
            etape(T.impotAjoute, `${eur(avec, lang)} − ${eur(sans, lang)}`, valeur, plancherZero(somme(avec, -sans))),
        ],
        valeur,
        sources: sourcesImpotFoyer(autresRevenus + imposable, foyer),
    };
}

/**
 * Un partage entre rémunération et dividendes, ligne par ligne : ce que chaque
 * euro traverse avant d'arriver sur le compte du dirigeant.
 *
 * @param parametres les paramètres de `partage` (forme, enveloppe, capital, activité, foyer)
 * @param point      le résultat de `partage` pour la part explorée
 */
export function expliquerPartage(parametres, point, lang = "fr") {
    const T = TEXTES_SOCIETE[lang] || TEXTES_SOCIETE.fr;
    const { forme, activite = "services", artisan = false, capital = 1000, foyer = {}, seuilSurBenefice = false } = parametres;
    const total = point.enveloppe;
    const part = point.part;
    const enveloppeRemuneration = total * part;
    const v = voletRemuneration({ forme, enveloppeRemuneration, activite, artisan });
    const sasu = forme === "sasu";
    const etapePart = etape(T.part, `${eur(total, lang)} × ${pct(part, lang)}`, enveloppeRemuneration, produit(total, part));

    // La rémunération, et ses cotisations.
    let remuneration;
    let cotisations;
    let etapeAvantImpot;
    if (sasu) {
        const c = v.detail;
        const surBrut = remplir(T.surBrut, { montant: eur(v.brut, lang) });
        const patronales = etape(T.patronales, surBrut, c.patronales);
        const salariales = etape(T.salariales, surBrut, c.salariales);
        remuneration = {
            etapes: [etapePart, patronales, etape(T.brutSasu, `${eur(enveloppeRemuneration, lang)} − ${eur(c.patronales, lang)}`, v.brut, somme(enveloppeRemuneration, -c.patronales))],
            sources: [S.assimile],
        };
        cotisations = {
            etapes: [patronales, salariales, etape(T.cotisationsSasu, `${eur(c.patronales, lang)} + ${eur(c.salariales, lang)}`, v.cotisations, somme(c.patronales, c.salariales))],
            sources: [S.assimile, S.pass],
        };
        etapeAvantImpot = etape(T.netSasu, `${eur(v.brut, lang)} − ${eur(c.salariales, lang)}`, v.avantImpot, somme(v.brut, -c.salariales));
    } else {
        const tns = etapesCotisationsIndependant(enveloppeRemuneration, activite, artisan, lang, T);
        etapeAvantImpot = etape(
            T.remunerationEurl,
            `${eur(enveloppeRemuneration, lang)} − ${eur(v.cotisations, lang)}`,
            v.avantImpot,
            plancherZero(somme(enveloppeRemuneration, -v.cotisations)),
        );
        remuneration = {
            etapes: [etapePart, etape(T.cotisationsTns, remplir(T.surRevenu, { montant: eur(enveloppeRemuneration, lang) }), v.cotisations), etapeAvantImpot],
            sources: [S.independants],
        };
        cotisations = { etapes: tns.etapes, sources: tns.sources };
    }

    // La société : son bénéfice, son impôt, ses dividendes.
    const etapeResultat = etape(T.resultat, `${eur(total, lang)} − ${eur(v.cout, lang)}`, point.resultatSociete, plancherZero(somme(total, -v.cout)));
    const etapeImpotSocietes = etapeIs(T.is, point.resultatSociete, lang);
    const etapeDividendes = etape(
        T.dividendes,
        `${eur(point.resultatSociete, lang)} − ${eur(point.impotSocietes, lang)}`,
        point.dividendes,
        plancherZero(somme(point.resultatSociete, -point.impotSocietes)),
    );
    const impotSocietes = { etapes: [etapeResultat, etapeImpotSocietes], sources: [S.is] };
    const dividendes = { etapes: [etapeResultat, etapeImpotSocietes, etapeDividendes], sources: [S.is] };

    // EURL : ce que les dividendes au-delà de 10 % du capital ajoutent aux cotisations.
    let cotisationsDividendes = null;
    const d = point.dividendes;
    const cotisants = point.dividendesCotisants;
    if (!sasu) {
        if (cotisants > 0) {
            const avec = v.cotisations + point.cotisationsDividendes;
            cotisationsDividendes = {
                etapes: [
                    // Entreprise individuelle à l'IS : les 10 % se comptent sur le
                    // bénéfice distribué, pas sur un capital.
                    etape(
                        seuilSurBenefice ? T.cotisantsBenefice : T.cotisants,
                        remplir(T.dixPourcent, { dividendes: eur(d, lang), capital: eur(seuilSurBenefice ? d : capital, lang) }),
                        cotisants,
                        plancherZero(somme(d, produit(-SEUIL_DIVIDENDES_EURL, seuilSurBenefice ? d : capital))),
                    ),
                    etape(T.cotisationsAvec, remplir(T.surRevenu, { montant: eur(enveloppeRemuneration + cotisants, lang) }), avec),
                    etape(T.cotisationsSans, remplir(T.surRevenu, { montant: eur(enveloppeRemuneration, lang) }), v.cotisations),
                    etape(T.cotisationsDividendes, `${eur(avec, lang)} − ${eur(v.cotisations, lang)}`, point.cotisationsDividendes, plancherZero(somme(avec, -v.cotisations))),
                ],
                sources: [S.dixPourcent, S.independants],
            };
        } else {
            cotisationsDividendes = { etapes: [etape(T.aucunCotisant, eur(0, lang), 0)], sources: [S.dixPourcent] };
        }
    }

    const prelevementsSociaux = {
        etapes: [
            cotisants > 0
                ? etape(T.prelevementsEurl, `(${eur(d, lang)} − ${eur(cotisants, lang)}) × ${pct(PFU_SOCIAL, lang)}`, point.prelevementsSociaux, produit(somme(d, -cotisants), PFU_SOCIAL))
                : etape(sasu ? T.prelevements : T.prelevementsEurl, `${eur(d, lang)} × ${pct(PFU_SOCIAL, lang)}`, point.prelevementsSociaux, produit(d, PFU_SOCIAL)),
        ],
        sources: sasu ? [S.pfu] : [S.pfu, S.dixPourcent],
    };

    // L'impôt sur le revenu : la rémunération au barème, les dividendes au
    // prélèvement forfaitaire ou, quand il gagne, au barème après abattement.
    const nonDeductible = v.detail.nonDeductible || 0;
    const etapesImpot = [];
    const sourcesImpot = [];
    if (v.imposable > 0) {
        etapesImpot.push(...etapesImposable(v.avantImpot, nonDeductible, lang, T));
        sourcesImpot.push(S.fraisPro);
    }
    if (point.imposition === "bareme") {
        const revenu = v.imposable + d * (1 - ABATTEMENT_DIVIDENDES_BAREME);
        // Sans rémunération, « 0 € + » n'apprend rien : seuls les dividendes restent.
        etapesImpot.push(
            v.imposable > 0
                ? etape(
                      T.imposableBareme,
                      `${eur(v.imposable, lang)} + ${eur(d, lang)} × ${pct(1 - ABATTEMENT_DIVIDENDES_BAREME, lang)}`,
                      revenu,
                      somme(v.imposable, produit(d, 1 - ABATTEMENT_DIVIDENDES_BAREME)),
                  )
                : etape(T.imposableDividendes, `${eur(d, lang)} × ${pct(1 - ABATTEMENT_DIVIDENDES_BAREME, lang)}`, revenu, produit(d, 1 - ABATTEMENT_DIVIDENDES_BAREME)),
        );
        const ir = etapesImpotAjoute(revenu, foyer, lang, T);
        etapesImpot.push(...ir.etapes);
        sourcesImpot.push(S.abattementDividendes, ...ir.sources);
    } else {
        const ir = etapesImpotAjoute(v.imposable, foyer, lang, T);
        etapesImpot.push(...ir.etapes);
        sourcesImpot.push(...ir.sources);
        if (d > 0) {
            etapesImpot.push(etape(T.pfuIr, `${eur(d, lang)} × ${pct(PFU_IR, lang)}`, d * PFU_IR, produit(d, PFU_IR)));
            etapesImpot.push(etape(T.impotTotal, `${eur(ir.valeur, lang)} + ${eur(d * PFU_IR, lang)}`, point.impotRevenu, somme(ir.valeur, d * PFU_IR)));
            sourcesImpot.push(S.pfu);
        }
    }
    const impotRevenu = { etapes: etapesImpot, sources: [...new Set(sourcesImpot)] };

    const termes = [v.avantImpot, d, -point.prelevementsSociaux, -point.cotisationsDividendes, -point.impotRevenu];
    const calculReste = `${eur(v.avantImpot, lang)} + ${eur(d, lang)} − ${eur(point.prelevementsSociaux, lang)}${
        sasu ? "" : ` − ${eur(point.cotisationsDividendes, lang)}`
    } − ${eur(point.impotRevenu, lang)}`;
    const etapeReste = etape(T.reste, calculReste, Math.max(0, point.net), plancherZero(somme(...termes)));
    const reste = { etapes: sasu ? [etapeAvantImpot, etapeReste] : [etapeReste], sources: [] };

    // Le résumé d'une carte : la dernière étape de chaque ligne, jusqu'au net.
    const derniere = (ex) => ex.etapes[ex.etapes.length - 1];
    const resume = {
        etapes: [
            etapeAvantImpot,
            etapeDividendes,
            derniere(prelevementsSociaux),
            ...(sasu ? [] : [derniere(cotisationsDividendes)]),
            derniere(impotRevenu),
            etapeReste,
        ],
        sources: [...new Set([...remuneration.sources, ...impotSocietes.sources, ...prelevementsSociaux.sources, ...(cotisationsDividendes?.sources || []), ...impotRevenu.sources])],
    };

    return { remuneration, cotisations, impotSocietes, dividendes, cotisationsDividendes, prelevementsSociaux, impotRevenu, reste, resume };
}

/**
 * Les indemnités kilométriques d'une année : la tranche du barème, son
 * coefficient et son montant fixe.
 *
 * @param c le résultat de `indemnites`
 */
export function expliquerIndemnitesKilometriques(c, { vehicule }, lang = "fr") {
    const T = TEXTES_SOCIETE[lang] || TEXTES_SOCIETE.fr;
    const seuils = BAREME_IK[vehicule].seuils;
    const nombre = (v, max = 0) => v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { maximumFractionDigits: max });
    const tranche = remplir(T.tranches[c.tranche], { bas: nombre(seuils[0]), basPlusUn: nombre(seuils[0] + 1), haut: nombre(seuils[1]) });
    const calcul = remplir(c.fixe ? T.ikFixe : T.ik, { kilometres: nombre(c.km), coefficient: nombre(c.coefficient, 3), fixe: nombre(c.fixe) });
    return {
        montant: {
            etapes: [
                etape(
                    remplir(T.ikLibelle, { tranche }),
                    calcul,
                    c.montant,
                    c.fixe ? somme(produit(c.km, c.coefficient), c.fixe) : produit(c.km, c.coefficient),
                    "centimes",
                ),
            ],
            sources: [S.kilometrique],
        },
    };
}

/**
 * Un arrêt maladie en société : l'indemnité par jour, le total et ce qu'il en
 * reste après CSG et CRDS.
 *
 * @param c       le résultat de `arretSasu` ou de `arretEurl`
 * @param entree  { salaireMensuel } en SASU, { revenuAnnuel } en EURL, et `jours`
 */
export function expliquerArretSociete(c, { salaireMensuel = 0, revenuAnnuel = 0, jours }, lang = "fr") {
    const T = TEXTES_SOCIETE[lang] || TEXTES_SOCIETE.fr;
    if (!c.ouvre) return { parJour: null, total: null, net: null };
    const s = IJ_SOCIETE.salarie;
    const t = IJ_SOCIETE.independant;
    let parJour;
    if (c.forme === "sasu") {
        const retenu = Math.min(Math.max(0, salaireMensuel), c.plafondMensuel);
        parJour = {
            etapes: [
                etape(
                    remplir(T.retenuSasu, { plafond: eurCentimes(c.plafondMensuel, lang) }),
                    plusPetit(eur(salaireMensuel, lang), eurCentimes(c.plafondMensuel, lang), lang),
                    retenu,
                    minimum(Math.max(0, salaireMensuel), c.plafondMensuel),
                    "centimes",
                ),
                etape(
                    T.parJourSasu,
                    `${pct(s.taux, lang)} × 3 × ${eurCentimes(retenu, lang)} ÷ ${nombreDecimal(s.diviseur, lang)}`,
                    c.parJour,
                    quotient(produit(s.taux, 3, retenu), s.diviseur),
                    "centimes",
                ),
            ],
            sources: [S.ijSalarie, S.smic],
        };
    } else {
        const revenu = Math.max(0, revenuAnnuel);
        const retenu = Math.min(revenu, c.plafond);
        parJour = {
            etapes: [
                etape(remplir(T.retenuEurl, { plafond: eur(c.plafond, lang) }), plusPetit(eur(revenu, lang), eur(c.plafond, lang), lang), retenu, minimum(revenu, c.plafond)),
                etape(T.parJourEurl, `${eur(retenu, lang)} ÷ ${t.diviseur}`, c.parJour, quotient(retenu, t.diviseur), "centimes"),
            ],
            sources: c.reglemente ? [S.ijFormule, S.ijMinimum, S.ijLiberalReglemente] : [S.ijFormule, S.ijMinimum, S.ijPlafond],
        };
    }
    const carence = IJ_SOCIETE.carence;
    const etapeJours = c.reglemente
        ? etape(remplir(T.joursReglemente, { carence }), plusPetit(`${jours} − ${carence}`, 87, lang), c.joursIndemnises, minimum(plancherZero(somme(jours, -carence)), t.joursMaxLiberalReglemente), "jours")
        : etape(remplir(T.joursCarence, { carence }), `${jours} − ${carence}`, c.joursIndemnises, plancherZero(somme(jours, -carence)), "jours");
    const etapeTotal = etape(T.totalArret, `${eurCentimes(c.parJour, lang)} × ${c.joursIndemnises}`, c.total, produit(c.parJour, c.joursIndemnises));
    return {
        parJour,
        total: { etapes: [etapeJours, etapeTotal], sources: c.reglemente ? [S.ijCarence, S.ijLiberalReglemente] : [S.ijCarence] },
        net: {
            etapes: [etapeTotal, etape(T.netArret, `${eur(c.total, lang)} × ${pct(1 - IJ_SOCIETE.prelevements, lang)}`, c.net, produit(c.total, 1 - IJ_SOCIETE.prelevements))],
            sources: [S.csgIj],
        },
    };
}

/** Un nombre décimal écrit dans la langue lue : 91,25 ou 91.25. */
function nombreDecimal(v, lang) {
    return v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { maximumFractionDigits: 3 });
}

const TEXTES_REGIMES = {
    fr: {
        disponible: "Ce qui reste du chiffre d'affaires, dépenses payées",
        benefice: "Bénéfice : chiffre d'affaires moins les dépenses",
        cotisationsSur: "Cotisations d'indépendant",
        imposableReel: "Revenu imposable : bénéfice moins cotisations, plus la CSG non déductible",
        netReel: "Bénéfice moins cotisations et impôt",
        netEurl: "Rémunération moins l'impôt",
        netSasuImpot: "Salaire net moins l'impôt",
        rienNeReste: "Rien ne reste : les dépenses dépassent ce que l'activité rapporte",
        brutSasu: "Salaire brut : ce qui reste, une fois payées les cotisations patronales",
    },
    en: {
        disponible: "What is left of turnover once expenses are paid",
        benefice: "Profit: turnover minus expenses",
        cotisationsSur: "Self-employed contributions",
        imposableReel: "Taxable income: profit minus contributions, plus non-deductible CSG",
        netReel: "Profit minus contributions and tax",
        netEurl: "Pay minus tax",
        netSasuImpot: "Net salary minus tax",
        rienNeReste: "Nothing is left: expenses exceed what the business brings in",
        brutSasu: "Gross salary: what is left once employer contributions are paid",
    },
};

/**
 * Ce que chaque régime du comparateur laisse, du chiffre d'affaires au net :
 * micro, entreprise individuelle au réel, EURL et SASU tout en rémunération.
 *
 * @param scenario   un scénario de `comparerRegimes`
 * @param parametres { chiffreAffaires, charges, activite, artisan, foyer }
 * @param options    `plancherZero` à false quand l'écran montre un net négatif
 */
export function expliquerRegime(scenario, parametres, lang = "fr", { plancherZero: auPlancher = true } = {}) {
    const ex = expliquerRegimeBrut(scenario, parametres, lang);
    // Le comparateur n'affiche jamais un net négatif : il écrit 0 €, et le
    // calcul le dit. La trajectoire, elle, montre la perte.
    if (auPlancher && scenario.net < 0) {
        const T = TEXTES_REGIMES[lang] || TEXTES_REGIMES.fr;
        ex.net.etapes.push(etape(T.rienNeReste, eur(scenario.net, lang), 0, plancherZero(scenario.net)));
    }
    return ex;
}

function expliquerRegimeBrut(scenario, { chiffreAffaires, charges = 0, activite = "services", artisan = false, foyer = {} }, lang) {
    const T = { ...(TEXTES_SOCIETE[lang] || TEXTES_SOCIETE.fr), ...(TEXTES_REGIMES[lang] || TEXTES_REGIMES.fr) };
    const disponible = Math.max(0, chiffreAffaires - charges);
    const surRevenu = (v) => remplir(T.surRevenu, { montant: eur(v, lang) });

    if (scenario.regime === "micro") {
        const m = expliquerMicro({ chiffreAffaires, activite, artisan, ...foyer }, lang, { frais: charges });
        return {
            net: {
                etapes: [...m.cotisations.etapes, ...m.cfp.etapes, ...m.impot.etapes, ...m.net.etapes],
                sources: [...new Set([...m.cotisations.sources, ...m.cfp.sources, ...m.impot.sources])],
            },
        };
    }

    // Sans dépenses, « 60 000 € − 0 € » n'apprend rien : l'étape disparaît.
    const etapeDisponible = (libelle) =>
        charges > 0 ? [etape(libelle, `${eur(chiffreAffaires, lang)} − ${eur(charges, lang)}`, disponible, plancherZero(somme(chiffreAffaires, -charges)))] : [];

    if (scenario.regime === "reel") {
        const c = scenario.detailCotisations;
        const imposable = Math.max(0, disponible - c.total + c.nonDeductible);
        const ir = etapesImpotAjoute(imposable, foyer, lang, T);
        return {
            net: {
                etapes: [
                    ...etapeDisponible(T.benefice),
                    etape(T.cotisationsSur, surRevenu(disponible), c.total),
                    etape(T.imposableReel, `${eur(disponible, lang)} − ${eur(c.total, lang)} + ${eur(c.nonDeductible, lang)}`, imposable, plancherZero(somme(disponible, -c.total, c.nonDeductible))),
                    ...ir.etapes,
                    etape(T.netReel, `${eur(disponible, lang)} − ${eur(c.total, lang)} − ${eur(scenario.impot, lang)}`, scenario.net, somme(disponible, -c.total, -scenario.impot)),
                ],
                sources: [S.independants, S.pass, ...ir.sources],
            },
        };
    }

    if (scenario.regime === "eurl") {
        const c = scenario.detailCotisations;
        const imposable = scenario.remuneration > 0 ? scenario.remuneration + c.nonDeductible - abattementFraisProfessionnels(scenario.remuneration + c.nonDeductible) : 0;
        const ir = etapesImpotAjoute(imposable, foyer, lang, T);
        return {
            net: {
                etapes: [
                    ...etapeDisponible(T.disponible),
                    etape(T.cotisationsSur, surRevenu(disponible), c.total),
                    etape(T.remunerationEurl, `${eur(disponible, lang)} − ${eur(c.total, lang)}`, scenario.remuneration, plancherZero(somme(disponible, -c.total))),
                    ...(scenario.remuneration > 0 ? etapesImposable(scenario.remuneration, c.nonDeductible, lang, T) : []),
                    ...ir.etapes,
                    etape(T.netEurl, `${eur(scenario.remuneration, lang)} − ${eur(scenario.impot, lang)}`, scenario.net, somme(scenario.remuneration, -scenario.impot)),
                ],
                sources: [S.independants, S.pass, S.fraisPro, ...ir.sources],
            },
        };
    }

    // SASU, tout en rémunération.
    const c = scenario.detailCotisations;
    const surBrut = remplir(T.surBrut, { montant: eur(scenario.remuneration, lang) });
    const imposable = Math.max(0, c.net + c.nonDeductible - abattementFraisProfessionnels(c.net + c.nonDeductible));
    const ir = etapesImpotAjoute(imposable, foyer, lang, T);
    return {
        net: {
            etapes: [
                ...etapeDisponible(T.disponible),
                etape(T.patronales, surBrut, c.patronales),
                etape(T.brutSasu, `${eur(disponible, lang)} − ${eur(c.patronales, lang)}`, scenario.remuneration, somme(disponible, -c.patronales)),
                etape(T.salariales, surBrut, c.salariales),
                etape(T.netSasu, `${eur(scenario.remuneration, lang)} − ${eur(c.salariales, lang)}`, c.net, somme(scenario.remuneration, -c.salariales)),
                ...(c.net > 0 ? etapesImposable(c.net, c.nonDeductible, lang, T) : []),
                ...ir.etapes,
                etape(T.netSasuImpot, `${eur(c.net, lang)} − ${eur(scenario.impot, lang)}`, scenario.net, somme(c.net, -scenario.impot)),
            ],
            sources: [S.assimile, S.pass, S.fraisPro, ...ir.sources],
        },
    };
}

const TEXTES_VERSEMENT = {
    fr: {
        versement: "Ce qui est sur le compte, moins ce que la société doit garder",
        beneficeApres: "Bénéfice de l'exercice, rémunération de l'exercice déduite",
        is: "Impôt sur les sociétés de l'exercice",
        acomptes: "Moins les acomptes déjà payés",
        cotisationsSasu: "Cotisations sur la rémunération nette de l'exercice, ce versement compris",
        cotisationsEurl: "Cotisations d'indépendant sur la rémunération de l'exercice, ce versement compris",
        surNet: "sur {montant} de rémunération nette",
        dejaSasu: "Moins celles des paies déjà faites",
        dejaEurl: "Moins celles déjà réglées à l'Urssaf",
    },
    en: {
        versement: "What is in the account, minus what the company must keep",
        beneficeApres: "Profit for the year, the year's pay deducted",
        is: "Corporate tax for the year",
        acomptes: "Minus the instalments already paid",
        cotisationsSasu: "Contributions on the year's net pay, this payment included",
        cotisationsEurl: "Self-employed contributions on the year's pay, this payment included",
        surNet: "on {montant} of net pay",
        dejaSasu: "Minus those on payslips already made",
        dejaEurl: "Minus those already paid to the Urssaf",
    },
};

/**
 * Ce que la société peut verser ce mois-ci : le solde, moins la TVA, l'impôt
 * et les cotisations qu'elle doit garder.
 *
 * @param p les paramètres de `versementPossible`
 * @param v son résultat
 */
export function expliquerVersementMensuel(p, v, lang = "fr") {
    const T = TEXTES_VERSEMENT[lang] || TEXTES_VERSEMENT.fr;
    const sasu = p.forme === "sasu";
    const commun = { forme: p.forme, activite: p.activite, artisan: p.artisan };
    const deja = coutPourNet({ ...commun, net: p.dejaVerse || 0 });
    const total = coutPourNet({ ...commun, net: (p.dejaVerse || 0) + v.versement });
    const payees = sasu ? deja.cotisations : Math.max(0, p.cotisationsPayees || 0);
    const acomptes = Math.max(0, p.acomptesIs || 0);

    const benefice = p.benefice - total.cout;
    const etapesImpot = [
        etape(T.beneficeApres, `${eur(p.benefice, lang)} − ${eur(total.cout, lang)}`, benefice, somme(p.benefice, -total.cout)),
        etapeIs(T.is, benefice, lang),
    ];
    if (acomptes > 0) {
        const brut = impotSocietes(benefice);
        etapesImpot.push(etape(T.acomptes, `${eur(brut, lang)} − ${eur(acomptes, lang)}`, v.impot, plancherZero(somme(brut, -acomptes))));
    }

    const etapesCotisations = [
        etape(sasu ? T.cotisationsSasu : T.cotisationsEurl, remplir(T.surNet, { montant: eur((p.dejaVerse || 0) + v.versement, lang) }), total.cotisations),
    ];
    if (payees > 0) {
        etapesCotisations.push(
            etape(sasu ? T.dejaSasu : T.dejaEurl, `${eur(total.cotisations, lang)} − ${eur(payees, lang)}`, v.cotisations, plancherZero(somme(total.cotisations, -payees))),
        );
    }

    // Seuls les montants retenus s'écrivent : un « − 0 € » n'apprend rien.
    const retenus = [v.tva, v.impot, v.cotisations, v.coussin].filter((x) => x > 0);
    return {
        versement: {
            etapes: [etape(T.versement, [eur(p.solde, lang), ...retenus.map((x) => eur(x, lang))].join(" − "), v.versement, somme(p.solde, ...retenus.map((x) => -x)))],
            sources: [],
        },
        impot: { etapes: etapesImpot, sources: [S.is] },
        cotisations: { etapes: etapesCotisations, sources: sasu ? [S.assimile, S.pass] : [S.independants, S.pass] },
    };
}

const TEXTES_OPTION_IS = {
    fr: {
        coutBesoin: "Ce que coûte à la société la rémunération qui vous laisse {besoin}",
        resultat: "Bénéfice qui reste dans la société",
        is: "Impôt sur les sociétés sur ce bénéfice",
        reserve: "Ce qui reste à la société après l'impôt",
        epargne: "Ce que l'impôt sur le revenu vous laisse, moins votre besoin",
        surBenefice: "sur {montant} de bénéfice",
    },
    en: {
        coutBesoin: "What the pay that leaves you {besoin} costs the company",
        resultat: "Profit left in the company",
        is: "Corporate tax on that profit",
        reserve: "What the company keeps after tax",
        epargne: "What income tax leaves you, minus your need",
        surBenefice: "on {montant} of profit",
    },
};

/**
 * Passer à l'impôt sur les sociétés : ce que laisse chaque option, et ce que
 * la société garde quand on ne se verse que son besoin.
 *
 * @param p      les paramètres de `comparerOptionIs`
 * @param calcul son résultat
 */
export function expliquerOptionIs(p, calcul, lang = "fr") {
    const T = TEXTES_OPTION_IS[lang] || TEXTES_OPTION_IS.fr;
    const { forme = "ei", benefice, capital = 1000, activite = "services", artisan = false, foyer = {}, besoin = null } = p;
    const ir = expliquerRegime(calcul.ir, { chiffreAffaires: benefice, charges: 0, activite, artisan, foyer }, lang, { plancherZero: false }).net;
    const is = expliquerPartage(
        { forme: "eurl", enveloppe: benefice, capital, activite, artisan, foyer, seuilSurBenefice: forme === "ei" },
        calcul.is.meilleur,
        lang,
    ).resume;
    const g = calcul.garde;
    if (!g?.atteignable) return { ir, is, reserve: null, epargne: null };
    const besoinRetenu = Math.min(besoin, benefice);
    const cout = benefice - g.resultat;
    return {
        ir,
        is,
        reserve: {
            etapes: [
                etape(remplir(T.coutBesoin, { besoin: eur(besoinRetenu, lang) }), remplir(T.surBenefice, { montant: eur(benefice, lang) }), cout),
                etape(T.resultat, `${eur(benefice, lang)} − ${eur(cout, lang)}`, g.resultat, plancherZero(somme(benefice, -cout))),
                etapeIs(T.is, g.resultat, lang),
                etape(T.reserve, `${eur(g.resultat, lang)} − ${eur(g.impotSocietes, lang)}`, g.reserve, somme(g.resultat, -g.impotSocietes)),
            ],
            sources: [S.independants, S.is],
        },
        epargne: {
            etapes: [etape(T.epargne, `${eur(calcul.ir.net, lang)} − ${eur(besoin, lang)}`, g.epargneIr, plancherZero(somme(calcul.ir.net, -besoin)))],
            sources: [],
        },
    };
}

const TEXTES_QUITTER = {
    fr: {
        micro: "Ce que la micro vous laisse",
        meilleur: "Ce que le meilleur régime vous laisse",
        gain: "Ce que quitter la micro rapporte par an",
    },
    en: {
        micro: "What the micro regime leaves you",
        meilleur: "What the best regime leaves you",
        gain: "What leaving the micro regime brings in a year",
    },
};

/**
 * Ce que quitter la micro rapporte : le net de la micro, celui du meilleur
 * régime, et leur écart, chacun avec la dernière étape de son calcul.
 *
 * @param comparaison le résultat de `comparerRegimes`
 * @param parametres  { chiffreAffaires, charges, activite, artisan, foyer }
 */
export function expliquerQuitterMicro(comparaison, parametres, lang = "fr") {
    const T = TEXTES_QUITTER[lang] || TEXTES_QUITTER.fr;
    const micro = comparaison.scenarios.find((s) => s.regime === "micro");
    const meilleur = comparaison.scenarios.find((s) => s.regime === comparaison.meilleurRegime);
    const exMicro = expliquerRegime(micro, parametres, lang, { plancherZero: false }).net;
    const exMeilleur = expliquerRegime(meilleur, parametres, lang, { plancherZero: false }).net;
    const derniere = (ex, libelle) => ({ ...ex.etapes[ex.etapes.length - 1], libelle });
    return {
        gain: {
            etapes: [
                derniere(exMicro, T.micro),
                derniere(exMeilleur, T.meilleur),
                etape(T.gain, `${eur(meilleur.net, lang)} − ${eur(micro.net, lang)}`, comparaison.gainSiChangement, somme(meilleur.net, -micro.net)),
            ],
            sources: [...new Set([...exMicro.sources, ...exMeilleur.sources])],
        },
    };
}

/* ------------------------------------------------------------------------- */
/* SaaS : combien d'abonnés pour en vivre                                     */
/* ------------------------------------------------------------------------- */

const TEXTES_SAAS = {
    fr: {
        commission: "Commission de Stripe sur un abonnement (1,5 % par paiement, 0,7 % pour Billing, et 0,25 € fixes)",
        chiffreAffaires: "Chiffre d'affaires de {abonnes} abonnés dans l'année",
        cotisations: "Chiffre d'affaires × taux de cotisations",
        cfp: "Chiffre d'affaires × taux de la contribution à la formation",
        impot: "Impôt sur ce bénéfice, pour une personne seule sans autre revenu (barème, décote)",
        sur: "sur {montant} de revenu imposable",
        commissions: "Commissions de Stripe dans l'année",
        frais: "Vos frais fixes dans l'année",
        net: "Ce qu'il vous reste dans l'année",
        parMois: "Ce qu'il vous reste par mois",
        abonnes: "Abonnés nécessaires : le plus petit nombre qui vous laisse au moins {objectif} par mois",
        unDeMoins: "avec {moins} abonnés, il ne vous resterait que {reste} par mois",
        zero: "votre objectif est déjà atteint sans abonné",
        nouveaux: "Abonnés à gagner chaque mois pour remplacer ceux qui partent",
        arrondi: "{abonnes} × {perte} ÷ 100, arrondi à l'abonné supérieur",
        charges: "Charges déduites par la société : commissions et frais",
        cotisationsSasu: "Cotisations patronales et salariales",
    },
    en: {
        commission: "Stripe's fee on one subscription (1.5% per payment, 0.7% for Billing, and a fixed €0.25)",
        chiffreAffaires: "Turnover from {abonnes} subscribers over the year",
        cotisations: "Turnover × contribution rate",
        cfp: "Turnover × training levy rate",
        impot: "Income tax on this profit, for a single person with no other income (scale, discount)",
        sur: "on {montant} of taxable income",
        commissions: "Stripe fees over the year",
        frais: "Your fixed costs over the year",
        net: "What you keep over the year",
        parMois: "What you keep each month",
        abonnes: "Subscribers needed: the smallest number that leaves you at least {objectif} a month",
        unDeMoins: "with {moins} subscribers, you would only keep {reste} a month",
        zero: "your goal is already reached without any subscriber",
        nouveaux: "Subscribers to win each month to replace those who leave",
        arrondi: "{abonnes} × {perte} ÷ 100, rounded up to the next subscriber",
        charges: "Expenses deducted by the company: fees and costs",
        cotisationsSasu: "Employer and employee contributions",
    },
};

/** Un prix d'abonnement s'écrit au centime s'il en porte : 9,99 € ne s'arrondit pas. */
const prixEcrit = (prix, lang) => (Number.isInteger(prix) ? eur(prix, lang) : eurCentimes(prix, lang));

/**
 * Les explications du simulateur SaaS : la commission de Stripe, le nombre
 * d'abonnés (le calcul de ce qu'il laisse, puis pourquoi un de moins ne
 * suffirait pas), chaque ligne de ce qu'on retire au chiffre d'affaires, ce
 * qu'il reste, et les abonnés à gagner chaque mois pour tenir.
 *
 * @param p les paramètres d'`abonnesSaas`
 * @param r son résultat, quand il est possible
 */
export function expliquerSaas({ prix, statut = "micro", activite, netMensuel, fraisMensuels = 0, pertePct }, r, lang = "fr") {
    const T = TEXTES_SAAS[lang] || TEXTES_SAAS.fr;
    const commission = commissionStripe(prix);
    const tauxStripe = STRIPE_CARTE_EEE + STRIPE_BILLING;
    const etapeCommission = etape(
        T.commission,
        `${prixEcrit(prix, lang)} × ${pct(tauxStripe, lang)} + ${eurCentimes(STRIPE_FIXE, lang)}`,
        commission,
        somme(produit(prix, tauxStripe), STRIPE_FIXE),
        "centimes",
    );
    const explications = { commission: { etapes: [etapeCommission], sources: ["stripe_tarifs"] } };
    if (!r?.possible) return explications;
    if (statut === "eurl" || statut === "sasu") return { ...explications, ...expliquerSaasSociete({ prix, statut, netMensuel, fraisMensuels, pertePct }, r, lang, etapeCommission) };

    const n = r.abonnes;
    const ca = r.chiffreAffaires;
    const fiscale = activiteFiscale(activite);
    const taux = TAUX_COTISATIONS[activite];
    const tcfp = tauxCfp(activite, false);
    const benefice = etapeBenefice(ca, fiscale, lang);
    const revenuImposable = beneficeMicro(ca, fiscale);

    const etapes = [
        etapeCommission,
        etape(remplir(T.chiffreAffaires, { abonnes: n }), `${n} × ${prixEcrit(prix, lang)} × 12`, ca, produit(n, prix, 12)),
        etape(T.cotisations, `${eur(ca, lang)} × ${pct(taux, lang)}`, r.cotisations, produit(ca, taux)),
        etape(T.cfp, `${eur(ca, lang)} × ${pct(tcfp, lang)}`, r.cfp, produit(ca, tcfp)),
        benefice.etape,
        etape(T.impot, remplir(T.sur, { montant: eur(revenuImposable, lang) }), r.impot),
        etape(T.commissions, `${n} × ${eurCentimes(commission, lang, 3)} × 12`, r.commissions, produit(n, commission, 12)),
    ];
    const termes = [ca, -r.cotisations, -r.cfp, -r.impot, -r.commissions];
    const ecrits = [eur(ca, lang), eur(r.cotisations, lang), eur(r.cfp, lang), eur(r.impot, lang), eur(r.commissions, lang)];
    if (fraisMensuels > 0) {
        etapes.push(etape(T.frais, `${eur(fraisMensuels, lang)} × 12`, r.frais, produit(fraisMensuels, 12)));
        termes.push(-r.frais);
        ecrits.push(eur(r.frais, lang));
    }
    etapes.push(etape(T.net, ecrits.join(" − "), r.net, somme(...termes)));
    etapes.push(etape(T.parMois, `${eur(r.net, lang)} ÷ 12`, r.net / 12, quotient(r.net, 12)));
    const moins = n > 0 ? bilanAnnuel(n - 1, { prix, activite, fraisMensuels }) : null;
    etapes.push(
        etape(
            remplir(T.abonnes, { objectif: eur(netMensuel, lang) }),
            moins ? remplir(T.unDeMoins, { moins: n - 1, reste: eur(moins.net / 12, lang) }) : T.zero,
            n,
            MOTEUR,
            "abonnes",
        ),
    );

    const sources = [SOURCE_TAUX[activite], "cfp_taux", ...benefice.sources, "bareme_ir_2026_complet", "stripe_tarifs"];
    if (impotBrut(revenuImposable, {}) - impotSurLeRevenu(revenuImposable, {}) > 0.5) sources.push("decote_ir_2026");

    // Les lignes de la cascade : cotisations, formation et impôt sont celles
    // du revenu net sur ce chiffre d'affaires, les deux outils ne peuvent pas
    // les écrire autrement.
    const micro = expliquerMicro({ chiffreAffaires: ca, activite }, lang);

    return {
        ...explications,
        abonnes: { etapes, sources },
        cotisations: micro.cotisations,
        cfp: micro.cfp,
        impot: micro.impot,
        commissions: {
            etapes: [
                etapeCommission,
                etape(T.commissions, `${n} × ${eurCentimes(commission, lang, 3)} × 12`, r.commissions, produit(n, commission, 12)),
            ],
            sources: ["stripe_tarifs"],
        },
        ...(fraisMensuels > 0
            ? { frais: { etapes: [etape(T.frais, `${eur(fraisMensuels, lang)} × 12`, r.frais, produit(fraisMensuels, 12))], sources: [] } }
            : {}),
        net: { etapes: [etape(T.net, ecrits.join(" − "), r.net, somme(...termes))], sources: [] },
        nouveaux: explicationNouveaux(n, pertePct, r, lang, T),
    };
}

/** Les abonnés à gagner chaque mois : commun aux trois formes. */
function explicationNouveaux(n, pertePct, r, lang, T) {
    return {
        etapes: [
            etape(
                T.nouveaux,
                remplir(T.arrondi, { abonnes: n, perte: lang === "fr" ? String(pertePct).replace(".", ",") : String(pertePct) }),
                r.nouveauxPourTenir,
                quotientArrondiSup(produit(n, pertePct), 100),
                "abonnes",
            ),
        ],
        sources: [],
    };
}

/**
 * Le simulateur SaaS en EURL ou en SASU : la commission et les frais sont des
 * charges de la société, retirées avant les cotisations. La suite est celle
 * du comparateur des statuts (expliquerRegime), rejouée sur ce qui reste.
 */
function expliquerSaasSociete({ prix, statut, netMensuel, fraisMensuels, pertePct }, r, lang, etapeCommission) {
    const T = TEXTES_SAAS[lang] || TEXTES_SAAS.fr;
    const TS = { ...(TEXTES_SOCIETE[lang] || TEXTES_SOCIETE.fr), ...(TEXTES_REGIMES[lang] || TEXTES_REGIMES.fr) };
    const n = r.abonnes;
    const ca = r.chiffreAffaires;
    const commission = commissionStripe(prix);
    const charges = r.commissions + r.frais;
    const regime = expliquerRegime(r.scenario, { chiffreAffaires: ca, charges, activite: "services", artisan: false, foyer: {} }, lang);
    const c = r.scenario.detailCotisations;
    const sasu = statut === "sasu";

    const etapeCommissions = etape(T.commissions, `${n} × ${eurCentimes(commission, lang, 3)} × 12`, r.commissions, produit(n, commission, 12));
    const etapeFrais = etape(T.frais, `${eur(fraisMensuels, lang)} × 12`, r.frais, produit(fraisMensuels, 12));
    const etapesCharges = [etapeCommissions];
    if (fraisMensuels > 0) {
        etapesCharges.push(etapeFrais, etape(T.charges, `${eur(r.commissions, lang)} + ${eur(r.frais, lang)}`, charges, somme(r.commissions, r.frais)));
    }

    const etapes = [
        etapeCommission,
        etape(remplir(T.chiffreAffaires, { abonnes: n }), `${n} × ${prixEcrit(prix, lang)} × 12`, ca, produit(n, prix, 12)),
        ...etapesCharges,
        ...regime.net.etapes,
        etape(T.parMois, `${eur(r.net, lang)} ÷ 12`, r.net / 12, quotient(r.net, 12)),
    ];
    const moins = n > 0 ? bilanAnnuel(n - 1, { prix, statut, fraisMensuels }) : null;
    etapes.push(
        etape(
            remplir(T.abonnes, { objectif: eur(netMensuel, lang) }),
            moins ? remplir(T.unDeMoins, { moins: n - 1, reste: eur(moins.net / 12, lang) }) : T.zero,
            n,
            MOTEUR,
            "abonnes",
        ),
    );

    // Les cotisations : celles du gérant sur tout ce qui reste, ou, en SASU,
    // les patronales puis les salariales du salaire brut.
    const disponible = Math.max(0, ca - charges);
    const cotisations = sasu
        ? {
              etapes: [
                  etape(TS.patronales, remplir(TS.surBrut, { montant: eur(r.remuneration, lang) }), c.patronales),
                  etape(TS.salariales, remplir(TS.surBrut, { montant: eur(r.remuneration, lang) }), c.salariales),
                  etape(T.cotisationsSasu, `${eur(c.patronales, lang)} + ${eur(c.salariales, lang)}`, r.cotisations, somme(c.patronales, c.salariales)),
              ],
              sources: [S.assimile, S.pass],
          }
        : {
              etapes: [etape(TS.cotisationsSur, remplir(TS.surRevenu, { montant: eur(disponible, lang) }), r.cotisations)],
              sources: [S.independants, S.pass],
          };

    // L'impôt : sur la rémunération nette, après l'abattement de 10 %.
    const net = sasu ? c.net : r.remuneration;
    const imposable = net > 0 ? net + c.nonDeductible - abattementFraisProfessionnels(net + c.nonDeductible) : 0;
    const ir = etapesImpotAjoute(imposable, {}, lang, TS);
    const impot = {
        etapes: [...(net > 0 ? etapesImposable(net, c.nonDeductible, lang, TS) : []), ...ir.etapes],
        sources: [S.fraisPro, ...ir.sources],
    };

    return {
        abonnes: { etapes, sources: [...new Set([...regime.net.sources, "stripe_tarifs"])] },
        cotisations,
        impot,
        commissions: { etapes: [etapeCommission, etapeCommissions], sources: ["stripe_tarifs"] },
        ...(fraisMensuels > 0 ? { frais: { etapes: [etapeFrais], sources: [] } } : {}),
        net: regime.net,
        nouveaux: explicationNouveaux(n, pertePct, r, lang, T),
    };
}
